import type {
  AiAssistResponse,
  AiConnectionResponse,
  AiTestingAccessResponse,
  ExtensionApprovalRequestResponse,
  ExtensionDraftSummary,
  ExtensionFile,
  ExtensionFileSummary,
  ExtensionInstallationSummary,
  ExtensionPackageSummary,
  ExtensionVersionSummary,
} from "@api-client";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { CodeEditor } from "@sylviq/ui-react-web/components/editor/CodeEditor";
import { FileExplorer } from "@sylviq/ui-react-web/components/editor/FileExplorer";
import { EditorWorkspace, WorkspaceExplorer, WorkspaceEditor, WorkspaceAssistant, WorkspaceDiagnostics } from "@sylviq/ui-react-web/components/editor/EditorWorkspace";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { Tabs, type TabItem } from "@sylviq/ui-react-web/components/ui/tabs";
import { type FormEvent, type ReactElement, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  type BuildDraftValidationReport,
  type BuildPackageRepo,
  type BuildPackageRepoFileSummary,
  type ApiTokenDeveloperScope,
  type CreatedOrganizationApiToken,
  type ExtensionCatalogPackage,
  type OrganizationApiToken,
  type McpOAuthGrant,
  buildBuildPackageRepo,
  createOrganizationApiToken,
  MCP_ENDPOINT_URL,
  MCP_RESOURCE_URI,
  deleteBuildDraft,
  deleteBuildDraftFile,
  createBuildDraft,
  getBuildDraftFile,
  getBuildPackageRepo,
  getBuildPackageRepoFile,
  linkBuildPackageRepo,
  listBuildPackageRepoFiles,
  listBuildDraftFiles,
  listBuildDrafts,
  listBuildCatalog,
  listBuildPackageVersions,
  listBuildPackages,
  listExtensionInstallations,
  listOrganizationApiTokens,
  listMcpOAuthGrants,
  listOrganizations,
  putBuildDraftFile,
  putBuildPackageRepoFile,
  publishBuildDraftDirect,
  releaseBuildPackageRepo,
  resolveExtensionDocsUrl,
  updateBuildExtension,
  getAiTestingAccess,
  requestAiAssist,
  revokeApiToken,
  revokeMcpOAuthGrant,
  validateBuildDraft,
} from "@/lib/api";
import { useSession } from "@/lib/session";
import { PersonalAiConnections } from "./PersonalAiConnections";
import { DirectBuildInstallation, installationCapabilities, installationTargetApps } from "./DirectBuildInstallation";
import { RepoFileTree } from "./RepoFileTree";
import { ExtensionsBrowsePanel } from "@/features/extensions/ExtensionsBrowsePanel";
import { ExtensionsCuratedPanel } from "@/features/extensions/ExtensionsCuratedPanel";
import { CuratedExtensionDetails } from "@/features/extensions/CuratedExtensionDetails";
import { ExtensionsInstalledPanel } from "@/features/extensions/ExtensionsInstalledPanel";
import { ExtensionsUpdatesPanel } from "@/features/extensions/ExtensionsUpdatesPanel";
import { notifyExtensionInstallationsChanged } from "@/features/extensions/extension-events";
import { type ExtensionUpdate, findExtensionUpdates } from "@/features/extensions/extension-updates";
import { OrganizationNavigation, organizationViewHref, organizationViews, type ConsoleOrganizationOption, type OrganizationView } from "@/features/organizations/OrganizationNavigation";

export type ExtensionsTab = "browse" | "installed" | "updates" | "curated";

const EXTENSIONS_HUB_TABS: TabItem[] = [
  { value: "browse", label: "Browse" },
  { value: "installed", label: "Installed" },
  { value: "updates", label: "Updates" },
  { value: "curated", label: "Curated" },
];

type ListState<T> =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; items: T[] };

type PackageVersionState = { status: "idle" } | ListState<ExtensionVersionSummary>;
type VersionCompareSelection = { first: string; second: string };

const API_TOKEN_LAST_USED_FORMATTER = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

function parseTokenLastUsedAt(value: string | null | undefined): Date | null {
  if (typeof value !== "string") return null;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function tokenLastUsedPresentation(value: string | null | undefined): ReactElement | string {
  if (value === null) return "Never used";
  const parsed = parseTokenLastUsedAt(value);
  if (!parsed) return "Unavailable";
  return (
    <time dateTime={value}>
      {API_TOKEN_LAST_USED_FORMATTER.format(parsed)}
    </time>
  );
}

type LoadedBuildState = {
  status: "loaded";
  organizationId: string;
  organizationName: string;
  organizations: ConsoleOrganizationOption[];
  organizationRole: string;
  organizationSlug: string;
  installations: ListState<ExtensionInstallationSummary>;
  packages: ListState<ExtensionPackageSummary>;
  catalog: ListState<ExtensionCatalogPackage>;
  drafts: ListState<ExtensionDraftSummary>;
  apiTokens: ListState<OrganizationApiToken>;
  oauthGrants: ListState<McpOAuthGrant>;
};

type BuildLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | LoadedBuildState;

type DraftFileReadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; file: ExtensionFile; value: string; stale: boolean };

type DraftValidationState =
  | { status: "idle" }
  | { status: "validating"; draftId: string }
  | { status: "error"; draftId: string; message: string }
  | {
      status: "report";
      draftId: string;
      revision: number;
      value: BuildDraftValidationReport;
    };

type PublicationRequestState =
  | { status: "idle" }
  | { status: "requesting"; draftId: string }
  | { status: "error"; draftId: string; message: string }
  | {
      status: "submitted";
      draftId: string;
      value: ExtensionApprovalRequestResponse;
    };

type DiscardIntent =
  | { kind: "close" }
  | { kind: "navigation"; href: string }
  | { kind: "sign-out" }
  | { kind: "select-file"; path: string }
  | { kind: "open-draft"; draft: ExtensionDraftSummary }
  | { kind: "close-repo" }
  | { kind: "repo-select-file"; path: string }
  | { kind: "open-repo"; package: RepoWorkspaceTarget };

type BuildOrganizationContext = Pick<LoadedBuildState, "organizationId">;

type RepoLinkState =
  | { status: "linked"; repo: BuildPackageRepo }
  | { status: "unlinked" }
  | { status: "error" };

type RepoWorkspaceState =
  | { status: "checking" }
  | { status: "error"; message: string }
  | { status: "unlinked" }
  | { status: "ready"; repo: BuildPackageRepo };

type RepoTreeState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; commit: string; files: BuildPackageRepoFileSummary[] };

type RepoFileReadState =
  | { status: "idle" }
  | { status: "loading"; path: string }
  | { status: "error"; path: string; message: string }
  | { status: "inert"; path: string; size: number | null }
  | { status: "loaded"; path: string; value: string; savedValue: string };

type RepoWorkspaceTarget = Pick<ExtensionPackageSummary, "id" | "publisher" | "key">;

type OneTimeTokenState = {
  response: CreatedOrganizationApiToken;
  createdAt: string | null;
};

type ApiTokenAudience = OrganizationApiToken["audience"];
function tokenAudienceName(audience: ApiTokenAudience): string {
  return { api: "API", mcp: "MCP", cli: "CLI" }[audience];
}

const DRAFTABLE_PACKAGE_STATUSES = new Set(["draft", "published"]);

const API_TOKEN_SCOPE_OPTIONS: {
  scope: ApiTokenDeveloperScope;
  label: string;
  detail: string;
}[] = [
  { scope: "build:read", label: "Read Build data", detail: "Read packages, drafts, installations, and versions." },
  { scope: "build:write", label: "Write Build data", detail: "Create and update supported Build resources." },
  { scope: "build:test", label: "Run Build tests", detail: "Request supported extension test operations." },
  { scope: "approvals:request", label: "Request approvals", detail: "Requires Build write; does not grant approval authority for posting approvals." },
  { scope: "extensions:manage", label: "Manage extension installations", detail: "Install, upgrade, enable, disable, and uninstall extensions in this organization. Owner-only automation grant." },
  { scope: "approvals:decide", label: "Decide posting approvals", detail: "Approve or reject reviewed posting approvals. Owner-only automation grant." },
];

function packageVersionKey(organizationId: string, packageId: string): string {
  return `${organizationId}:${packageId}`;
}

function formatInertJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return "This value could not be displayed.";
  }
}

type AiAccessState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; access: AiTestingAccessResponse };

type AiProposalContext = {
  userId: string;
  organizationId: string;
  draftId: string;
  editorRequestId: number;
  path: string;
  source: string;
};

type AiAssistantState =
  | { status: "idle" }
  | { status: "requesting" }
  | { status: "error"; message: string }
  | { status: "proposal"; context: AiProposalContext; value: AiAssistResponse };

const AI_PROMPT_MAX_BYTES = 8 * 1024;
const AI_FILE_MAX_BYTES = 32 * 1024;
const AI_PATH_MAX_BYTES = 512;

function statusCode(error: unknown): number | null {
  if (typeof error !== "object" || error === null || !("status" in error)) return null;
  return typeof error.status === "number" ? error.status : null;
}

function isRevisionConflict(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  return "status" in error && "code" in error &&
    error.status === 409 && error.code === "revision_conflict";
}

function isGitReleaseConflict(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  return "status" in error && "code" in error &&
    error.status === 409 && error.code === "git_release_conflict";
}

function repositoryFailureMessage(error: unknown, fallback: string): string {
  switch (statusCode(error)) {
    case 403:
      return "You do not have permission to change this repository.";
    case 413:
      return "This exceeds the repository's file size limit.";
    case 415:
      return "This file type cannot be handled as text.";
    case 422:
      return "The repository rejected this content as invalid.";
    default:
      return fallback;
  }
}

function shortCommit(commit: string): string {
  return commit.slice(0, 7);
}

/** Mirrors the API's canonical tag rule: optional `v`, `MAJOR.MINOR.PATCH`, optional `-prerelease`. */
function isCanonicalReleaseTag(tag: string): boolean {
  if (tag.length === 0 || tag.length > 255) return false;
  const version = tag.startsWith("v") ? tag.slice(1) : tag;
  if (version.length === 0 || version.length > 64) return false;
  const separator = version.indexOf("-");
  const core = separator === -1 ? version : version.slice(0, separator);
  const prerelease = separator === -1 ? null : version.slice(separator + 1);
  const parts = core.split(".");
  if (parts.length !== 3 || !parts.every(isCanonicalNumericTagPart)) return false;
  if (prerelease !== null) {
    if (prerelease.length === 0) return false;
    for (const identifier of prerelease.split(".")) {
      if (identifier.length === 0 || !/^[0-9A-Za-z-]+$/.test(identifier)) return false;
      if (/^\d+$/.test(identifier) && !isCanonicalNumericTagPart(identifier)) return false;
    }
  }
  return true;
}

function isCanonicalNumericTagPart(part: string): boolean {
  return /^\d+$/.test(part) && (part === "0" || !part.startsWith("0"));
}

function isSafeDraftFilePath(path: string): boolean {
  return Boolean(path) &&
    path.length <= 240 &&
    !path.startsWith("/") &&
    !path.includes("\\") &&
    path.split("/").every(
      (segment) => segment && segment !== "." && segment !== ".." && !segment.startsWith("."),
    );
}

function languageForFilePath(path: string): string {
  const extension = path.split(".").at(-1)?.toLowerCase();
  const languages: Record<string, string> = {
    js: "javascript",
    jsx: "javascript",
    ts: "typescript",
    tsx: "typescript",
  };
  return extension ? (languages[extension] ?? "plaintext") : "plaintext";
}

async function readList<T>(
  read: () => Promise<T[]>,
  message: string,
): Promise<ListState<T>> {
  try {
    return { status: "loaded", items: await read() };
  } catch {
    return { status: "error", message };
  }
}

export function OrganizationBuildPage({
  organizationId,
  view = "extensions",
  extensionsTab = "browse",
  onExtensionsTabChange,
}: {
  organizationId: string;
  view?: Exclude<OrganizationView, "entities" | "data" | "secrets">;
  extensionsTab?: ExtensionsTab;
  onExtensionsTabChange?: (tab: ExtensionsTab) => void;
}) {
  const { state, signOut } = useSession();
  const [loadState, setLoadState] = useState<BuildLoadState>({
    status: "loading",
  });
  const [creatingDraftFor, setCreatingDraftFor] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [extensionUpdateBusyId, setExtensionUpdateBusyId] = useState<string | null>(null);
  const [extensionUpdateNotice, setExtensionUpdateNotice] = useState<{
    tone: "status" | "alert";
    message: string;
  } | null>(null);
  const [apiTokenName, setApiTokenName] = useState("");
  const [apiTokenAudience, setApiTokenAudience] = useState<ApiTokenAudience>("api");
  const [apiTokenExpiryDays, setApiTokenExpiryDays] = useState("30");
  const [apiTokenScopes, setApiTokenScopes] = useState<ApiTokenDeveloperScope[]>([
    "build:read",
  ]);
  const [apiTokenBusy, setApiTokenBusy] = useState(false);
  const [apiTokenNotice, setApiTokenNotice] = useState<string | null>(null);
  const [oneTimeToken, setOneTimeToken] = useState<OneTimeTokenState | null>(null);
  const [revokeTokenIntent, setRevokeTokenIntent] = useState<OrganizationApiToken | null>(null);
  const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null);
  const [revokeMcpGrantIntent, setRevokeMcpGrantIntent] = useState<McpOAuthGrant | null>(null);
  const [revokingMcpGrantId, setRevokingMcpGrantId] = useState<string | null>(null);
  const [mcpGrantNotice, setMcpGrantNotice] = useState<string | null>(null);
  const [packageVersions, setPackageVersions] = useState<
    Record<string, PackageVersionState>
  >({});
  const [versionCompareSelections, setVersionCompareSelections] = useState<
    Record<string, VersionCompareSelection>
  >({});
  const [activeDraft, setActiveDraft] = useState<ExtensionDraftSummary | null>(
    null,
  );
  const [draftDeleteIntent, setDraftDeleteIntent] = useState<ExtensionDraftSummary | null>(null);
  const [draftDeleteBusyId, setDraftDeleteBusyId] = useState<string | null>(null);
  const [draftDeleteNotice, setDraftDeleteNotice] = useState<{
    tone: "status" | "alert";
    message: string;
  } | null>(null);
  const [draftFiles, setDraftFiles] = useState<ListState<ExtensionFileSummary> | null>(
    null,
  );
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);
  const [draftFile, setDraftFile] = useState<DraftFileReadState>({
    status: "idle",
  });
  const [aiAccess, setAiAccess] = useState<AiAccessState>({ status: "loading" });
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiModelSource, setAiModelSource] = useState<"hosted" | "byo">("hosted");
  const [aiConnection, setAiConnection] = useState<AiConnectionResponse | null>(null);
  const [aiAssistant, setAiAssistant] = useState<AiAssistantState>({ status: "idle" });
  const [aiAssistantNotice, setAiAssistantNotice] = useState<string | null>(null);
  const aiRequestSequence = useRef(0);
  const aiRequestController = useRef<AbortController | null>(null);
  const aiRequestBusy = useRef(false);
  const aiProposalPending = useRef(false);
  const [draftValidation, setDraftValidation] = useState<DraftValidationState>({
    status: "idle",
  });
  const [publicationRequest, setPublicationRequest] =
    useState<PublicationRequestState>({ status: "idle" });
  const [fileMutationBusy, setFileMutationBusy] = useState(false);
  const [fileMutationError, setFileMutationError] = useState<string | null>(null);
  const [fileSavedNotice, setFileSavedNotice] = useState<string | null>(null);
  const [newFilePath, setNewFilePath] = useState("");
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [discardIntent, setDiscardIntent] = useState<DiscardIntent | null>(null);
  const [repoLinks, setRepoLinks] = useState<Record<string, RepoLinkState>>({});
  const [activeRepoPackage, setActiveRepoPackage] = useState<RepoWorkspaceTarget | null>(null);
  const [repoWorkspace, setRepoWorkspace] = useState<RepoWorkspaceState>({ status: "checking" });
  const [repoFiles, setRepoFiles] = useState<RepoTreeState | null>(null);
  const [repoSelectedPath, setRepoSelectedPath] = useState<string | null>(null);
  const [repoFile, setRepoFile] = useState<RepoFileReadState>({ status: "idle" });
  const [repoNewFilePath, setRepoNewFilePath] = useState("");
  const [repoCommitMessage, setRepoCommitMessage] = useState("");
  const [repoMutationBusy, setRepoMutationBusy] = useState(false);
  const [repoBuildBusy, setRepoBuildBusy] = useState(false);
  const [repoLinkBusy, setRepoLinkBusy] = useState(false);
  const [repoMutationError, setRepoMutationError] = useState<string | null>(null);
  const [repoNotice, setRepoNotice] = useState<string | null>(null);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseTag, setReleaseTag] = useState("v1.0.0");
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [releasePendingApprovalId, setReleasePendingApprovalId] = useState<string | null>(null);
  const editorRequestId = useRef(0);
  const repoRequestId = useRef(0);
  const activeOrganizationKey = useRef<string | null>(null);
  const curatedAuthoringLoaded = useRef(new Set<string>());
  const curatedLoadGeneration = useRef(0);
  const aiContextUserId = state.status === "authenticated" ? state.user.id : null;

  function stopAiRequest() {
    aiRequestSequence.current += 1;
    aiRequestController.current?.abort();
    aiRequestController.current = null;
    aiRequestBusy.current = false;
  }

  function invalidateAiAssistant(message?: string) {
    const hadProposalOrRequest = aiRequestBusy.current || aiProposalPending.current;
    stopAiRequest();
    aiProposalPending.current = false;
    setAiAssistant({ status: "idle" });
    setAiAssistantNotice(message && hadProposalOrRequest ? message : null);
  }

  function closeRepoReleaseDialog() {
    setReleaseDialogOpen(false);
    setReleaseTag("v1.0.0");
    setReleaseBusy(false);
    setReleaseError(null);
    setReleasePendingApprovalId(null);
  }

  function closeRepoWorkspace() {
    repoRequestId.current += 1;
    setActiveRepoPackage(null);
    setRepoWorkspace({ status: "checking" });
    setRepoFiles(null);
    setRepoSelectedPath(null);
    setRepoFile({ status: "idle" });
    setRepoNewFilePath("");
    setRepoCommitMessage("");
    setRepoMutationBusy(false);
    setRepoBuildBusy(false);
    setRepoLinkBusy(false);
    setRepoMutationError(null);
    setRepoNotice(null);
    closeRepoReleaseDialog();
  }

  useLayoutEffect(() => {
    invalidateAiAssistant();
    setAiPrompt("");
    setAiConnection(null);
    setAiModelSource("hosted");
  }, [organizationId, organizationId, aiContextUserId]);

  useEffect(() => {
    if (view !== "extensions" || extensionsTab !== "curated" || state.status !== "authenticated") return;
    let current = true;
    setAiAccess({ status: "loading" });
    void getAiTestingAccess().then((access) => {
      if (current) setAiAccess({ status: "loaded", access });
    }).catch(() => {
      if (current) setAiAccess({ status: "error" });
    });
    return () => {
      current = false;
      stopAiRequest();
    };
  }, [organizationId, organizationId, view, extensionsTab, state.status === "authenticated" ? state.user.id : null]);

  useEffect(() => () => stopAiRequest(), []);

  useEffect(() => {
    curatedAuthoringLoaded.current.clear();
    curatedLoadGeneration.current += 1;
    if (state.status !== "authenticated") {
      invalidateAiAssistant();
      setAiPrompt("");
      activeOrganizationKey.current = null;
      setOneTimeToken(null);
      setRevokeTokenIntent(null);
      setRevokeMcpGrantIntent(null);
      closeRepoWorkspace();
      setRepoLinks({});
      return;
    }

    let cancelled = false;
    invalidateAiAssistant();
    activeOrganizationKey.current = null;
    editorRequestId.current += 1;
    setActiveDraft(null);
    setDraftDeleteIntent(null);
    setDraftDeleteBusyId(null);
    setDraftDeleteNotice(null);
    setDraftFiles(null);
    setSelectedFilePath(null);
    setDraftFile({ status: "idle" });
    setDraftValidation({ status: "idle" });
    setPublicationRequest({ status: "idle" });
    setApiTokenName("");
    setApiTokenAudience("api");
    setApiTokenExpiryDays("30");
    setApiTokenScopes(["build:read"]);
    setApiTokenBusy(false);
    setApiTokenNotice(null);
    setOneTimeToken(null);
    setRevokeTokenIntent(null);
    setRevokeMcpGrantIntent(null);
    setRevokingMcpGrantId(null);
    setMcpGrantNotice(null);
    setRevokingTokenId(null);
    setPackageVersions({});
    setVersionCompareSelections({});
    setFileMutationError(null);
    setFileSavedNotice(null);
    closeRepoWorkspace();
    setRepoLinks({});
    setLoadState({ status: "loading" });

    void (async () => {
      try {
        const organizations = await listOrganizations();
        if (cancelled) return;
        const targetOrganization = organizations.find(
          (organization) => organization.id === organizationId,
        );
        if (!targetOrganization) {
          setLoadState({
            status: "error",
            message: "You do not have access to this organization.",
          });
          return;
        }

        activeOrganizationKey.current = targetOrganization.id;

        setLoadState({
          status: "loaded",
          organizationId: targetOrganization.id,
          organizationName: targetOrganization.name,
          organizations: organizations.map((organization) => ({
            id: organization.id,
            name: organization.name,
            canManageSecrets: organization.can_manage_secrets,
          })),
          organizationRole: targetOrganization.role,
          organizationSlug: targetOrganization.slug,
          installations: { status: "loading" },
          packages: { status: "loading" },
          catalog: { status: "loading" },
          drafts: { status: "loading" },
          apiTokens: { status: "loading" },
          oauthGrants: { status: "loading" },
        });

        const [installations, catalog, apiTokens, oauthGrants] = await Promise.all([
          view === "extensions" ? readList(
            () => listExtensionInstallations(targetOrganization.id),
            "Installed extensions could not be loaded. Please try again.",
          ) : Promise.resolve({ status: "loaded" as const, items: [] }),
          view === "extensions" ? readList(
            () => listBuildCatalog(targetOrganization.id),
            "Available extensions could not be loaded. Please try again.",
          ) : Promise.resolve({ status: "loaded" as const, items: [] }),
          view === "access" ? readList(
            () => listOrganizationApiTokens(targetOrganization.id),
            "API tokens could not be loaded. Please try again.",
          ) : Promise.resolve({ status: "loaded" as const, items: [] }),
          view === "access" ? readList(
            () => listMcpOAuthGrants(),
            "MCP OAuth grants could not be loaded. This feature may not be enabled here.",
          ) : Promise.resolve({ status: "loaded" as const, items: [] }),
        ]);
        if (!cancelled) {
          setLoadState((current) =>
            current.status === "loaded" && current.organizationId === targetOrganization.id
              ? { ...current, installations, catalog, apiTokens, oauthGrants }
              : current,
          );
        }
      } catch {
        if (cancelled) return;
        setLoadState({
          status: "error",
          message: "This organization could not be loaded. Please try again.",
        });
      }
    })();

    return () => {
      cancelled = true;
      curatedLoadGeneration.current += 1;
      activeOrganizationKey.current = null;
    };
  }, [organizationId, view, state]);

  useEffect(() => {
    if (view !== "extensions" || extensionsTab !== "curated") return;
    if (state.status !== "authenticated" || loadState.status !== "loaded") return;
    const targetOrganizationId = loadState.organizationId;
    const key = targetOrganizationId;
    if (curatedAuthoringLoaded.current.has(key)) return;
    curatedAuthoringLoaded.current.add(key);
    const generation = curatedLoadGeneration.current;
    void (async () => {
      const [packages, drafts] = await Promise.all([
        readList(
          () => listBuildPackages(targetOrganizationId),
          "Packages could not be loaded. Please try again.",
        ),
        readList(
          () => listBuildDrafts(targetOrganizationId),
          "Drafts could not be loaded. Please try again.",
        ),
      ]);
      if (generation !== curatedLoadGeneration.current) return;
      setLoadState((current) =>
        current.status === "loaded" && current.organizationId === targetOrganizationId
          ? { ...current, packages, drafts }
          : current,
      );
      if (packages.status === "loaded") {
        await refreshRepoLinks(
          { organizationId: targetOrganizationId },
          packages.items,
        );
      }
    })();
  }, [view, extensionsTab, loadState, state]);

  useEffect(() => {
    const draftDirty =
      draftFile.status === "loaded" && draftFile.value !== draftFile.file.source;
    const repoDirty =
      repoFile.status === "loaded" && repoFile.value !== repoFile.savedValue;
    if (!draftDirty && !repoDirty) return;
    const protect = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", protect);
    return () => window.removeEventListener("beforeunload", protect);
  }, [draftFile, repoFile]);

  if (state.status !== "authenticated") return null;

  const extensionUpdates =
    loadState.status === "loaded" &&
    loadState.installations.status === "loaded" &&
    loadState.catalog.status === "loaded"
      ? findExtensionUpdates(loadState.installations.items, loadState.catalog.items)
      : [];

  const refreshBuildLists = async (organization: BuildOrganizationContext) => {
    setLoadState((current) =>
      current.status === "loaded" && current.organizationId === organization.organizationId
        ? {
            ...current,
            packages: { status: "loading" },
            drafts: { status: "loading" },
          }
        : current,
    );

    const [packages, drafts] = await Promise.all([
      readList(
        () => listBuildPackages(organization.organizationId),
        "Packages could not be loaded. Please try again.",
      ),
      readList(
        () => listBuildDrafts(organization.organizationId),
        "Drafts could not be loaded. Please try again.",
      ),
    ]);

    setLoadState((current) =>
      current.status === "loaded" && current.organizationId === organization.organizationId
        ? { ...current, packages, drafts }
      : current,
    );
    if (packages.status === "loaded") {
      await refreshRepoLinks(organization, packages.items);
    }
    return { packages, drafts };
  };

  const refreshInstallations = async (organization: BuildOrganizationContext) => {
    setLoadState((current) =>
      current.status === "loaded" && current.organizationId === organization.organizationId
        ? { ...current, installations: { status: "loading" } }
        : current,
    );
    const loaded = await readList(
      () => listExtensionInstallations(organization.organizationId),
      "Installed extensions could not be loaded. Please try again.",
    );
    // Uninstalled rows stay as preserved history for the API, but they are not
    // installations this organization can manage or see as installed. Reinstall
    // creates a fresh row.
    const installations: typeof loaded =
      loaded.status === "loaded"
        ? {
            ...loaded,
            items: loaded.items.filter((item) => item.state !== "uninstalled"),
          }
        : loaded;
    setLoadState((current) =>
      current.status === "loaded" && current.organizationId === organization.organizationId
        ? { ...current, installations }
        : current,
    );
    if (installations.status === "loaded") {
      notifyExtensionInstallationsChanged();
    }
    return installations;
  };

  const refreshApiTokens = async (organization: BuildOrganizationContext) => {
    const key = organization.organizationId;
    setLoadState((current) =>
      current.status === "loaded" && current.organizationId === organization.organizationId
        ? { ...current, apiTokens: { status: "loading" } }
        : current,
    );
    const tokens = await readList(
      () => listOrganizationApiTokens(organization.organizationId),
      "API tokens could not be loaded. Please try again.",
    );
    if (activeOrganizationKey.current === key) {
      setLoadState((current) =>
        current.status === "loaded" && current.organizationId === organization.organizationId
          ? { ...current, apiTokens: tokens }
          : current,
      );
    }
    return tokens;
  };

  const refreshMcpOAuthGrants = async (organization: BuildOrganizationContext) => {
    const key = organization.organizationId;
    setLoadState((current) =>
      current.status === "loaded" && current.organizationId === organization.organizationId
        ? { ...current, oauthGrants: { status: "loading" } }
        : current,
    );
    const grants = await readList(
      () => listMcpOAuthGrants(),
      "MCP OAuth grants could not be loaded. Please try again.",
    );
    if (activeOrganizationKey.current === key) {
      setLoadState((current) =>
        current.status === "loaded" && current.organizationId === organization.organizationId
          ? { ...current, oauthGrants: grants }
          : current,
      );
    }
    return grants;
  };

  const updateInstalledExtension = async (update: ExtensionUpdate) => {
    const organization = currentProjectContext();
    if (!organization || extensionUpdateBusyId !== null) return;
    setExtensionUpdateBusyId(update.installation.id);
    setExtensionUpdateNotice(null);
    try {
      await updateBuildExtension(organization.organizationId, {
        organization_id: organization.organizationId,
        package_id: update.package.id,
        version: update.latest.version,
        apps: update.installation.apps,
        operation: "upgrade",
      });
      const refreshed = await refreshInstallations(organization);
      setExtensionUpdateNotice(
        refreshed.status === "loaded"
          ? {
              tone: "status",
              message: `Updated ${update.installation.publisher}/${update.installation.key} to ${update.latest.version}.`,
            }
          : {
              tone: "alert",
              message: "The update was applied, but the installed list could not be refreshed. Refresh to confirm its state.",
            },
      );
    } catch (error) {
      const code =
        typeof error === "object" && error !== null && "code" in error
          ? error.code
          : null;
      setExtensionUpdateNotice({
        tone: "alert",
        message:
          code === "dependency_review_required"
            ? "This update requires new dependencies. Install the newer release from Browse instead."
            : statusCode(error) === 403
              ? "Only an organization owner can update extensions."
              : statusCode(error) === 409
                ? "This update conflicts with the current installation. Refresh and try again."
                : "The extension could not be updated. Refresh and try again.",
      });
    } finally {
      setExtensionUpdateBusyId(null);
    }
  };

  const refreshRepoLinks = async (
    organization: BuildOrganizationContext,
    packages: ExtensionPackageSummary[],
  ) => {
    const key = organization.organizationId;
    const links = await Promise.all(
      packages.map(async (extensionPackage): Promise<[string, RepoLinkState]> => {
        try {
          const repo = await getBuildPackageRepo(
            organization.organizationId,
            extensionPackage.id,
          );
          return [extensionPackage.id, { status: "linked", repo }];
        } catch (error) {
          return [
            extensionPackage.id,
            statusCode(error) === 404 ? { status: "unlinked" } : { status: "error" },
          ];
        }
      }),
    );
    if (activeOrganizationKey.current !== key) return;
    setRepoLinks(Object.fromEntries(links));
  };

  const refreshRepoTree = async (
    organization: BuildOrganizationContext,
    packageId: string,
    requestId = repoRequestId.current,
  ): Promise<RepoTreeState | null> => {
    setRepoFiles({ status: "loading" });
    try {
      const tree = await listBuildPackageRepoFiles(
        organization.organizationId,
        packageId,
      );
      if (repoRequestId.current !== requestId) return null;
      const state: RepoTreeState = {
        status: "loaded",
        commit: tree.commit,
        files: tree.files,
      };
      setRepoFiles(state);
      return state;
    } catch {
      if (repoRequestId.current !== requestId) return null;
      const state: RepoTreeState = {
        status: "error",
        message: "Repository files could not be loaded. Please try again.",
      };
      setRepoFiles(state);
      return state;
    }
  };

  const openRepoWorkspace = async (extensionPackage: RepoWorkspaceTarget) => {
    const organization = currentProjectContext();
    if (!organization) return;
    closeDraftEditor();
    const requestId = ++repoRequestId.current;
    setActiveRepoPackage(extensionPackage);
    setRepoWorkspace({ status: "checking" });
    setRepoFiles(null);
    setRepoSelectedPath(null);
    setRepoFile({ status: "idle" });
    setRepoNewFilePath("");
    setRepoCommitMessage("");
    setRepoMutationBusy(false);
    setRepoBuildBusy(false);
    setRepoLinkBusy(false);
    setRepoMutationError(null);
    setRepoNotice(null);
    closeRepoReleaseDialog();
    try {
      const repo = await getBuildPackageRepo(
        organization.organizationId,
        extensionPackage.id,
      );
      if (repoRequestId.current !== requestId) return;
      setRepoWorkspace({ status: "ready", repo });
      setRepoLinks((current) => ({
        ...current,
        [extensionPackage.id]: { status: "linked", repo },
      }));
      await refreshRepoTree(organization, extensionPackage.id, requestId);
    } catch (error) {
      if (repoRequestId.current !== requestId) return;
      if (statusCode(error) === 404) {
        setRepoWorkspace({ status: "unlinked" });
      } else {
        setRepoWorkspace({
          status: "error",
          message: "The repository could not be loaded. Please try again.",
        });
      }
    }
  };

  const requestOpenRepo = (extensionPackage: RepoWorkspaceTarget) => {
    if (
      fileMutationBusy ||
      validationBusy ||
      publicationBusy ||
      repoMutationBusy ||
      repoBuildBusy ||
      repoLinkBusy ||
      releaseBusy
    ) {
      return;
    }
    if (activeRepoPackage?.id === extensionPackage.id) return;
    if (hasUnsavedChanges || repoHasUnsavedChanges) {
      setDiscardIntent({ kind: "open-repo", package: extensionPackage });
      return;
    }
    void openRepoWorkspace(extensionPackage);
  };

  const requestCloseRepoWorkspace = () => {
    if (repoMutationBusy || repoBuildBusy || repoLinkBusy || releaseBusy) return;
    if (repoHasUnsavedChanges) {
      setDiscardIntent({ kind: "close-repo" });
      return;
    }
    closeRepoWorkspace();
  };

  const loadRepoFile = async (path: string, requestId = repoRequestId.current) => {
    const organization = currentProjectContext();
    if (!organization || !activeRepoPackage) return;
    setRepoSelectedPath(path);
    setRepoFile({ status: "loading", path });
    setRepoCommitMessage(`Update ${path}`);
    setRepoMutationError(null);
    setRepoNotice(null);
    try {
      const file = await getBuildPackageRepoFile(
        organization.organizationId,
        activeRepoPackage.id,
        path,
      );
      if (repoRequestId.current !== requestId) return;
      setRepoFile({ status: "loaded", path, value: file.text, savedValue: file.text });
    } catch (error) {
      if (repoRequestId.current !== requestId) return;
      const status = statusCode(error);
      if (status === 415 || status === 422) {
        const size =
          repoFiles?.status === "loaded"
            ? repoFiles.files.find((file) => file.path === path)?.size ?? null
            : null;
        setRepoFile({ status: "inert", path, size });
      } else {
        setRepoFile({
          status: "error",
          path,
          message: "This file could not be loaded. Please try again.",
        });
      }
    }
  };

  const requestSelectRepoFile = (path: string) => {
    if (!activeRepoPackage || repoMutationBusy || repoBuildBusy || path === repoSelectedPath) {
      return;
    }
    if (repoHasUnsavedChanges) {
      setDiscardIntent({ kind: "repo-select-file", path });
      return;
    }
    void loadRepoFile(path);
  };

  const commitRepoFile = async () => {
    const organization = currentProjectContext();
    if (
      !organization ||
      !activeRepoPackage ||
      repoFile.status !== "loaded" ||
      !repoHasUnsavedChanges ||
      repoMutationBusy ||
      repoBuildBusy
    ) {
      return;
    }
    const editing = repoFile;
    const packageId = activeRepoPackage.id;
    const requestId = repoRequestId.current;
    const message = repoCommitMessage.trim();
    setRepoMutationBusy(true);
    setRepoMutationError(null);
    setRepoNotice(null);
    try {
      const result = await putBuildPackageRepoFile(
        organization.organizationId,
        packageId,
        { path: editing.path, text: editing.value, ...(message ? { message } : {}) },
      );
      if (repoRequestId.current !== requestId) return;
      setRepoFile({
        status: "loaded",
        path: editing.path,
        value: editing.value,
        savedValue: editing.value,
      });
      setRepoNotice(`Committed. Current commit ${shortCommit(result.commit)}.`);
      await refreshRepoTree(organization, packageId, requestId);
    } catch (error) {
      if (repoRequestId.current === requestId) {
        setRepoMutationError(
          repositoryFailureMessage(error, "Could not commit this file. Please try again."),
        );
      }
    } finally {
      if (repoRequestId.current === requestId) setRepoMutationBusy(false);
    }
  };

  const createRepoFile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const organization = currentProjectContext();
    if (
      !organization ||
      !activeRepoPackage ||
      repoWorkspace.status !== "ready" ||
      repoFiles?.status !== "loaded" ||
      repoMutationBusy ||
      repoBuildBusy
    ) {
      return;
    }
    const path = repoNewFilePath.trim();
    if (!isSafeDraftFilePath(path)) {
      setRepoMutationError("Use a relative file path without hidden or parent directories.");
      return;
    }
    if (repoFiles.files.some((file) => file.path === path)) {
      setRepoMutationError("That file already exists in this repository.");
      return;
    }
    const packageId = activeRepoPackage.id;
    const requestId = repoRequestId.current;
    setRepoMutationBusy(true);
    setRepoMutationError(null);
    setRepoNotice(null);
    try {
      const result = await putBuildPackageRepoFile(
        organization.organizationId,
        packageId,
        { path, text: "" },
      );
      if (repoRequestId.current !== requestId) return;
      setRepoNewFilePath("");
      if (!repoHasUnsavedChanges) {
        setRepoSelectedPath(path);
        setRepoCommitMessage(`Update ${path}`);
        setRepoFile({ status: "loaded", path, value: "", savedValue: "" });
      }
      setRepoNotice(`File created. Current commit ${shortCommit(result.commit)}.`);
      await refreshRepoTree(organization, packageId, requestId);
    } catch (error) {
      if (repoRequestId.current === requestId) {
        setRepoMutationError(
          repositoryFailureMessage(error, "Could not create this file. Please try again."),
        );
      }
    } finally {
      if (repoRequestId.current === requestId) setRepoMutationBusy(false);
    }
  };

  const buildRepoDistribution = async () => {
    const organization = currentProjectContext();
    if (!organization || !activeRepoPackage || repoBuildBusy || repoMutationBusy) return;
    const packageId = activeRepoPackage.id;
    const requestId = repoRequestId.current;
    setRepoBuildBusy(true);
    setRepoMutationError(null);
    setRepoNotice(null);
    try {
      const result = await buildBuildPackageRepo(
        organization.organizationId,
        packageId,
      );
      if (repoRequestId.current !== requestId) return;
      setRepoNotice(
        `Built ${result.filesWritten} ${result.filesWritten === 1 ? "file" : "files"} (${result.totalBytes} bytes) into ${result.distFolder}.`,
      );
      await refreshRepoTree(organization, packageId, requestId);
    } catch (error) {
      if (repoRequestId.current === requestId) {
        setRepoMutationError(
          repositoryFailureMessage(error, "The distribution could not be built. Please try again."),
        );
      }
    } finally {
      if (repoRequestId.current === requestId) setRepoBuildBusy(false);
    }
  };

  const linkRepoWorkspace = async () => {
    const organization = currentProjectContext();
    if (!organization || !activeRepoPackage || repoLinkBusy || repoMutationBusy) return;
    const extensionPackage = activeRepoPackage;
    const requestId = repoRequestId.current;
    setRepoLinkBusy(true);
    setRepoMutationError(null);
    setRepoNotice(null);
    try {
      const repo = await linkBuildPackageRepo(
        organization.organizationId,
        extensionPackage.id,
      );
      if (repoRequestId.current !== requestId) return;
      setRepoWorkspace({ status: "ready", repo });
      setRepoLinks((current) => ({
        ...current,
        [extensionPackage.id]: { status: "linked", repo },
      }));
      setRepoNotice("Repository linked.");
      await refreshRepoTree(organization, extensionPackage.id, requestId);
    } catch (error) {
      if (repoRequestId.current === requestId) {
        setRepoMutationError(
          statusCode(error) === 403
            ? "Only an organization owner can link a repository."
            : "The repository could not be linked. Please try again.",
        );
      }
    } finally {
      if (repoRequestId.current === requestId) setRepoLinkBusy(false);
    }
  };

  const submitRepoRelease = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const organization = currentProjectContext();
    if (!organization || !activeRepoPackage || releaseBusy) return;
    const tag = releaseTag.trim();
    if (!isCanonicalReleaseTag(tag)) {
      setReleaseError("Version must look like v1.2.3 (optional v, major.minor.patch, optional -prerelease).");
      return;
    }
    const packageId = activeRepoPackage.id;
    const requestId = repoRequestId.current;
    setReleaseBusy(true);
    setReleaseError(null);
    setReleasePendingApprovalId(null);
    try {
      const result = await releaseBuildPackageRepo(
        organization.organizationId,
        packageId,
        tag,
      );
      if (repoRequestId.current !== requestId) return;
      if (result.status === "published") {
        closeRepoReleaseDialog();
        setRepoNotice(`Published version ${result.version}.`);
        await refreshRepoTree(organization, packageId, requestId);
        await refreshBuildLists(organization);
      } else {
        setReleasePendingApprovalId(result.approval_id);
      }
    } catch (error) {
      if (repoRequestId.current !== requestId) return;
      if (isGitReleaseConflict(error)) {
        setReleaseError(`Version ${tag} already exists. Choose a new version.`);
      } else if (statusCode(error) === 409) {
        setReleaseError("This release conflicts with the repository's current state. Refresh and try again.");
      } else {
        setReleaseError("The release could not be prepared. Please try again.");
      }
    } finally {
      if (repoRequestId.current === requestId) setReleaseBusy(false);
    }
  };

  const startDraft = async (extensionPackage: ExtensionPackageSummary) => {
    if (
      loadState.status !== "loaded" ||
      creatingDraftFor !== null ||
      !DRAFTABLE_PACKAGE_STATUSES.has(extensionPackage.status)
    ) {
      return;
    }

    const organization = loadState;
    setActionError(null);
    setCreatingDraftFor(extensionPackage.id);
    try {
      await createBuildDraft(organization.organizationId, {
        organization_id: organization.organizationId,
        package_id: extensionPackage.id,
      });
      await refreshBuildLists(organization);
    } catch {
      setActionError("Could not create draft. Please try again.");
    } finally {
      setCreatingDraftFor(null);
    }
  };

  const hasUnsavedChanges =
    draftFile.status === "loaded" && draftFile.value !== draftFile.file.source;
  const repoHasUnsavedChanges =
    repoFile.status === "loaded" && repoFile.value !== repoFile.savedValue;
  const repoBusy =
    repoMutationBusy || repoBuildBusy || repoLinkBusy || releaseBusy;

  const currentProjectContext = (): BuildOrganizationContext | null =>
    loadState.status === "loaded"
      ? {
          organizationId: loadState.organizationId,
        }
      : null;

  const toggleApiTokenScope = (scope: ApiTokenDeveloperScope) => {
    setApiTokenScopes((current) => {
      const next = new Set(current);
      if (next.has(scope)) {
        next.delete(scope);
        if (scope === "build:write") next.delete("approvals:request");
      } else {
        next.add(scope);
        if (scope === "approvals:request") next.add("build:write");
      }
      return API_TOKEN_SCOPE_OPTIONS
        .map((option) => option.scope)
        .filter((candidate) => next.has(candidate));
    });
  };

  const submitApiToken = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const organization = currentProjectContext();
    const name = apiTokenName.trim();
    const expiresInDays = Number(apiTokenExpiryDays);
    const scopes: ApiTokenDeveloperScope[] =
      apiTokenAudience === "mcp" ? ["build:read"] : apiTokenScopes;
    if (
      !organization ||
      apiTokenBusy ||
      !name ||
      name.length > 80 ||
      !Number.isInteger(expiresInDays) ||
      expiresInDays < 1 ||
      expiresInDays > 365 ||
      scopes.length === 0
    ) {
      return;
    }

    const organizationKey = organization.organizationId;
    const request = {
      organization_id: organization.organizationId,
      name,
      ...(apiTokenAudience === "mcp" ? { audience: "mcp" as const } : {}),
      expires_in_days: expiresInDays,
      scopes,
    };
    setApiTokenBusy(true);
    setApiTokenNotice(null);
    setOneTimeToken(null);
    try {
      const response = await createOrganizationApiToken(request);
      if (activeOrganizationKey.current !== organizationKey) return;
      setOneTimeToken({ response, createdAt: null });
      setApiTokenName("");
      setApiTokenAudience("api");
      setApiTokenScopes(["build:read"]);
        setApiTokenExpiryDays("30");
      const refreshed = await refreshApiTokens(organization);
      if (activeOrganizationKey.current !== organizationKey) return;
      if (refreshed.status === "loaded") {
        const record = refreshed.items.find((item) => item.id === response.id);
        if (record) {
          setOneTimeToken((current) =>
            current?.response.token === response.token
              ? { ...current, createdAt: record.created_at }
              : current,
          );
        }
        setApiTokenNotice("Token created. Copy the secret now; it will not be shown again.");
      } else {
        setApiTokenNotice("Token created. The token list could not be refreshed; refresh it to verify the record.");
      }
    } catch {
      if (activeOrganizationKey.current !== organizationKey) return;
      const refreshed = await refreshApiTokens(organization);
      if (activeOrganizationKey.current !== organizationKey) return;
      setApiTokenNotice(
        refreshed.status === "loaded"
          ? "The token may have been created. Check the list before trying again; do not submit again until you verify it."
          : "The result could not be confirmed. Check the list before trying again; do not submit again until you verify it.",
      );
    } finally {
      setApiTokenBusy(false);
    }
  };

  const confirmTokenRevocation = async () => {
    const organization = currentProjectContext();
    const token = revokeTokenIntent;
    if (
      !organization ||
      !token ||
      token.organization_id !== organization.organizationId ||
      revokingTokenId !== null
    ) {
      return;
    }
    const organizationKey = organization.organizationId;
    setRevokingTokenId(token.id);
    setApiTokenNotice(null);
    try {
      await revokeApiToken(token.id);
      setRevokeTokenIntent(null);
      const refreshed = await refreshApiTokens(organization);
      if (activeOrganizationKey.current !== organizationKey) return;
      setApiTokenNotice(
        refreshed.status === "loaded"
          ? "Token revoked."
          : "The server accepted revocation, but the token list could not be refreshed. Refresh to confirm.",
      );
    } catch {
      if (activeOrganizationKey.current !== organizationKey) return;
      setRevokeTokenIntent(null);
      await refreshApiTokens(organization);
      if (activeOrganizationKey.current === organizationKey) {
        setApiTokenNotice("Could not confirm revocation. Refresh the list to check the token’s current state.");
      }
    } finally {
      setRevokingTokenId(null);
    }
  };

  const confirmMcpOAuthGrantRevocation = async () => {
    const organization = currentProjectContext();
    const grant = revokeMcpGrantIntent;
    if (!organization || !grant || revokingMcpGrantId !== null) return;
    const organizationKey = organization.organizationId;
    setRevokingMcpGrantId(grant.id);
    setMcpGrantNotice(null);
    try {
      await revokeMcpOAuthGrant(grant.id);
      setRevokeMcpGrantIntent(null);
      const refreshed = await refreshMcpOAuthGrants(organization);
      if (activeOrganizationKey.current === organizationKey) {
        setMcpGrantNotice(
          refreshed.status === "loaded"
            ? "OAuth grant revoked."
            : "Revocation was accepted, but the grant list could not be refreshed. Refresh to confirm.",
        );
      }
    } catch {
      if (activeOrganizationKey.current !== organizationKey) return;
      setRevokeMcpGrantIntent(null);
      const refreshed = await refreshMcpOAuthGrants(organization);
      if (activeOrganizationKey.current === organizationKey) {
        setMcpGrantNotice(
          refreshed.status === "loaded"
            ? "Could not confirm revocation. Check whether the grant is still listed before retrying."
            : "Could not confirm revocation. Refresh the grant list before retrying.",
        );
      }
    } finally {
      setRevokingMcpGrantId(null);
    }
  };

  const loadPackageVersions = async (extensionPackage: ExtensionPackageSummary) => {
    const organization = currentProjectContext();
    if (!organization) return;
    const key = packageVersionKey(
      organization.organizationId,
      extensionPackage.id,
    );
    setPackageVersions((current) => ({
      ...current,
      [key]: { status: "loading" },
    }));
    setVersionCompareSelections((current) => ({
      ...current,
      [key]: { first: "", second: "" },
    }));
    try {
      const items = await listBuildPackageVersions(
        organization.organizationId,
        extensionPackage.id,
      );
      setPackageVersions((current) => ({
        ...current,
        [key]: { status: "loaded", items },
      }));
    } catch {
      setPackageVersions((current) => ({
        ...current,
        [key]: {
          status: "error",
          message: "Versions could not be loaded. Please try again.",
        },
      }));
    }
  };

  const updateVersionCompareSelection = (
    key: string,
    side: keyof VersionCompareSelection,
    value: string,
  ) => {
    setVersionCompareSelections((current) => ({
      ...current,
      [key]: {
        ...(current[key] ?? { first: "", second: "" }),
        [side]: value,
      },
    }));
  };

  const loadDraftFile = async (
    draftId: string,
    path: string,
    requestId = editorRequestId.current,
  ) => {
    const organization = currentProjectContext();
    if (!organization || editorRequestId.current !== requestId) return;
    invalidateAiAssistant();
    setAiPrompt("");
    setSelectedFilePath(path);
    setDraftFile({ status: "loading" });
    setFileMutationError(null);
    setFileSavedNotice(null);
    try {
      const file = await getBuildDraftFile(
        organization.organizationId,
        draftId,
        path,
      );
      if (editorRequestId.current !== requestId) return;
      if (activeDraft?.id === draftId && activeDraft.revision !== file.revision) {
        setDraftValidation({ status: "idle" });
      }
      setActiveDraft((current) =>
        current?.id === draftId ? { ...current, revision: file.revision } : current,
      );
      setDraftFile({ status: "loaded", file, value: file.source, stale: false });
    } catch {
      if (editorRequestId.current !== requestId) return;
      setDraftFile({
        status: "error",
        message: "This file could not be loaded. Please try again.",
      });
    }
  };

  const refreshDraftFileList = async (
    organization: BuildOrganizationContext,
    draftId: string,
    requestId = editorRequestId.current,
  ): Promise<ExtensionFileSummary[] | null> => {
    setDraftFiles({ status: "loading" });
    try {
      const items = await listBuildDraftFiles(
        organization.organizationId,
        draftId,
      );
      if (editorRequestId.current !== requestId) return null;
      setDraftFiles({ status: "loaded", items });
      return items;
    } catch {
      if (editorRequestId.current !== requestId) return null;
      setDraftFiles({
        status: "error",
        message: "Draft files could not be loaded. Please try again.",
      });
      return null;
    }
  };

  const beginOpenDraft = async (draft: ExtensionDraftSummary) => {
    const organization = currentProjectContext();
    if (!organization || fileMutationBusy) return;
    closeRepoWorkspace();
    invalidateAiAssistant();
    setAiPrompt("");
    const requestId = ++editorRequestId.current;
    setDraftValidation({ status: "idle" });
    setPublicationRequest({ status: "idle" });
    setActiveDraft(draft);
    setDraftFiles({ status: "loading" });
    setSelectedFilePath(null);
    setDraftFile({ status: "idle" });
    setFileMutationError(null);
    setFileSavedNotice(null);
    try {
      const files = await listBuildDraftFiles(
        organization.organizationId,
        draft.id,
      );
      if (editorRequestId.current !== requestId) return;
      setDraftFiles({ status: "loaded", items: files });
      if (files.length > 0) {
        await loadDraftFile(draft.id, files[0].path, requestId);
      }
    } catch {
      if (editorRequestId.current !== requestId) return;
      setDraftFiles({
        status: "error",
        message: "Draft files could not be loaded. Please try again.",
      });
    }
  };

  const openDraftOrRepository = (draft: ExtensionDraftSummary) => {
    // A linked repository owns authoring for its package; the D1 draft editor
    // stays the fallback for packages without one.
    if (repoLinks[draft.package.id]?.status === "linked") {
      void openRepoWorkspace({
        id: draft.package.id,
        publisher: draft.package.publisher,
        key: draft.package.key,
      });
      return;
    }
    void beginOpenDraft(draft);
  };

  const requestOpenDraft = (draft: ExtensionDraftSummary) => {
    if (fileMutationBusy || validationBusy || publicationBusy || repoBusy || activeDraft?.id === draft.id) return;
    if (hasUnsavedChanges || repoHasUnsavedChanges) {
      setDiscardIntent({ kind: "open-draft", draft });
      return;
    }
    openDraftOrRepository(draft);
  };

  const requestSelectFile = (path: string) => {
    if (!activeDraft || fileMutationBusy || validationBusy || publicationBusy || path === selectedFilePath) return;
    if (hasUnsavedChanges) {
      setDiscardIntent({ kind: "select-file", path });
      return;
    }
    void loadDraftFile(activeDraft.id, path);
  };

  const closeDraftEditor = () => {
    invalidateAiAssistant();
    setAiPrompt("");
    editorRequestId.current += 1;
    setDraftValidation({ status: "idle" });
    setPublicationRequest({ status: "idle" });
    setActiveDraft(null);
    setDraftFiles(null);
    setSelectedFilePath(null);
    setDraftFile({ status: "idle" });
    setFileMutationError(null);
    setFileSavedNotice(null);
  };

  const requestCloseDraftEditor = () => {
    if (fileMutationBusy || validationBusy || publicationBusy) return;
    if (hasUnsavedChanges) {
      setDiscardIntent({ kind: "close" });
      return;
    }
    closeDraftEditor();
  };

  const discardAndContinue = () => {
    const intent = discardIntent;
    setDiscardIntent(null);
    if (!intent) return;
    if (intent.kind === "close") {
      closeDraftEditor();
    } else if (intent.kind === "close-repo") {
      closeRepoWorkspace();
    } else if (intent.kind === "navigation") {
      closeDraftEditor();
      window.location.assign(intent.href);
    } else if (intent.kind === "sign-out") {
      closeDraftEditor();
      void signOut();
    } else if (intent.kind === "open-draft") {
      openDraftOrRepository(intent.draft);
    } else if (intent.kind === "open-repo") {
      void openRepoWorkspace(intent.package);
    } else if (intent.kind === "repo-select-file") {
      void loadRepoFile(intent.path);
    } else if (activeDraft) {
      void loadDraftFile(activeDraft.id, intent.path);
    }
  };

  const reloadCurrentFile = () => {
    if (!activeDraft || !selectedFilePath || fileMutationBusy || publicationBusy) return;
    void loadDraftFile(activeDraft.id, selectedFilePath);
  };

  const handleFileMutationError = (error: unknown, fallback: string) => {
    if (isRevisionConflict(error)) {
      setDraftValidation({ status: "idle" });
      setFileMutationError(
        draftFile.status === "loaded"
          ? "This draft changed since you opened it. Reload the current file to review the latest version before continuing."
          : "This draft changed since you opened it. Close and reopen the draft to review its latest files.",
      );
      setDraftFile((current) =>
        current.status === "loaded" ? { ...current, stale: true } : current,
      );
    } else {
      setFileMutationError(fallback);
    }
    setFileSavedNotice(null);
  };

  const saveDraftFile = async () => {
    const organization = currentProjectContext();
    if (
      !organization ||
      !activeDraft ||
      draftFile.status !== "loaded" ||
      draftFile.stale ||
      !hasUnsavedChanges ||
      fileMutationBusy ||
      publicationBusy
    ) {
      return;
    }
    invalidateAiAssistant();
    const editingFile = draftFile;
    const requestId = editorRequestId.current;
    setFileMutationBusy(true);
    setDraftValidation({ status: "idle" });
    setFileMutationError(null);
    setFileSavedNotice(null);
    try {
      const result = await putBuildDraftFile(
        organization.organizationId,
        activeDraft.id,
        editingFile.file.revision,
        {
          organization_id: organization.organizationId,
          path: editingFile.file.path,
          source: editingFile.value,
        },
      );
      if (editorRequestId.current !== requestId) return;
      const savedFile: ExtensionFile = {
        ...editingFile.file,
        content_hash: result.content_hash ?? editingFile.file.content_hash,
        revision: result.revision,
        source: editingFile.value,
      };
      setActiveDraft((current) =>
        current?.id === activeDraft.id
          ? { ...current, revision: result.revision }
          : current,
      );
      setDraftFile({
        status: "loaded",
        file: savedFile,
        value: savedFile.source,
        stale: false,
      });
      setFileSavedNotice("Saved.");
      await Promise.all([
        refreshBuildLists(organization),
        refreshDraftFileList(organization, activeDraft.id, requestId),
      ]);
    } catch (error) {
      if (editorRequestId.current === requestId) {
        handleFileMutationError(error, "Could not save this file. Please try again.");
      }
    } finally {
      setFileMutationBusy(false);
    }
  };

  const createDraftFile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const organization = currentProjectContext();
    if (!organization || !activeDraft || fileMutationBusy || publicationBusy || draftFiles?.status !== "loaded") {
      return;
    }
    const path = newFilePath.trim();
    if (!isSafeDraftFilePath(path)) {
      setFileMutationError("Use a relative file path without hidden or parent directories.");
      return;
    }
    if (draftFiles.items.some((file) => file.path === path)) {
      setFileMutationError("That file already exists in this draft.");
      return;
    }
    if (draftFile.status === "loaded" && draftFile.stale) return;

    const requestId = editorRequestId.current;
    const previousFile = draftFile.status === "loaded" ? draftFile : null;
    const shouldSelectNewFile = !hasUnsavedChanges;
    setFileMutationBusy(true);
    setDraftValidation({ status: "idle" });
    setFileMutationError(null);
    setFileSavedNotice(null);
    try {
      const result = await putBuildDraftFile(
        organization.organizationId,
        activeDraft.id,
        previousFile?.file.revision ?? activeDraft.revision,
        { organization_id: organization.organizationId, path, source: "" },
      );
      if (editorRequestId.current !== requestId) return;
      setActiveDraft((current) =>
        current?.id === activeDraft.id
          ? { ...current, revision: result.revision }
          : current,
      );
      if (previousFile && !shouldSelectNewFile) {
        setDraftFile({
          ...previousFile,
          file: { ...previousFile.file, revision: result.revision },
        });
      } else {
        const createdFile: ExtensionFile = {
          path,
          content_hash: result.content_hash ?? "",
          source: "",
          revision: result.revision,
        };
        setSelectedFilePath(path);
        setDraftFile({ status: "loaded", file: createdFile, value: "", stale: false });
      }
      setNewFilePath("");
      setFileSavedNotice("File created.");
      await Promise.all([
        refreshBuildLists(organization),
        refreshDraftFileList(organization, activeDraft.id, requestId),
      ]);
    } catch (error) {
      if (editorRequestId.current === requestId) {
        handleFileMutationError(error, "Could not create this file. Please try again.");
      }
    } finally {
      setFileMutationBusy(false);
    }
  };

  const deleteDraftFile = async () => {
    const organization = currentProjectContext();
    if (
      !organization ||
      !activeDraft ||
      draftFile.status !== "loaded" ||
      draftFile.stale ||
      hasUnsavedChanges ||
      fileMutationBusy ||
      publicationBusy
    ) {
      return;
    }
    const deletingFile = draftFile.file;
    const draftId = activeDraft.id;
    const requestId = editorRequestId.current;
    setFileMutationBusy(true);
    setDraftValidation({ status: "idle" });
    setFileMutationError(null);
    setFileSavedNotice(null);
    try {
      const result = await deleteBuildDraftFile(
        organization.organizationId,
        draftId,
        deletingFile.revision,
        { organization_id: organization.organizationId, path: deletingFile.path },
      );
      if (editorRequestId.current !== requestId) return;
      setDeleteConfirmationOpen(false);
      setActiveDraft((current) =>
        current?.id === draftId ? { ...current, revision: result.revision } : current,
      );
      setSelectedFilePath(null);
      setDraftFile({ status: "idle" });
      setFileSavedNotice("File deleted.");
      const files = await refreshDraftFileList(organization, draftId, requestId);
      await refreshBuildLists(organization);
      if (files?.[0] && editorRequestId.current === requestId) {
        await loadDraftFile(draftId, files[0].path, requestId);
      }
    } catch (error) {
      if (editorRequestId.current === requestId) {
        setDeleteConfirmationOpen(false);
        handleFileMutationError(error, "Could not delete this file. Please try again.");
      }
    } finally {
      setFileMutationBusy(false);
    }
  };

  const validationBusy =
    draftValidation.status === "validating" &&
    draftValidation.draftId === activeDraft?.id;
  const currentValidation =
    draftValidation.status === "report" &&
    draftValidation.draftId === activeDraft?.id &&
    draftValidation.revision === activeDraft?.revision;
  const currentValidationIsValid =
    draftValidation.status === "report" &&
    draftValidation.draftId === activeDraft?.id &&
    draftValidation.revision === activeDraft?.revision &&
    draftValidation.value.valid;
  const publicationBusy =
    publicationRequest.status === "requesting" &&
    publicationRequest.draftId === activeDraft?.id;
  const publicationAlreadyRequested =
    publicationRequest.status === "submitted" &&
    publicationRequest.draftId === activeDraft?.id;
  const canRequestPublication = Boolean(
    currentProjectContext() &&
      activeDraft &&
      currentValidationIsValid &&
      draftFiles?.status === "loaded" &&
      draftFile.status === "loaded" &&
      !draftFile.stale &&
      !hasUnsavedChanges &&
      !fileMutationBusy &&
      !validationBusy &&
      !publicationBusy &&
      !publicationAlreadyRequested,
  );

  const requestDraftDeletion = (draft: ExtensionDraftSummary) => {
    if (
      !currentProjectContext() ||
      draftDeleteBusyId !== null ||
      fileMutationBusy ||
      validationBusy ||
      publicationBusy
    ) {
      return;
    }
    if (activeDraft?.id === draft.id && hasUnsavedChanges) {
      setDraftDeleteNotice({
        tone: "alert",
        message: "Save or discard unsaved changes before deleting this draft.",
      });
      return;
    }
    setDraftDeleteNotice(null);
    setDraftDeleteIntent(draft);
  };

  const confirmDraftDeletion = async () => {
    const organization = currentProjectContext();
    const draft = draftDeleteIntent;
    if (!organization || !draft || draftDeleteBusyId !== null) return;
    if (activeDraft?.id === draft.id && hasUnsavedChanges) {
      setDraftDeleteIntent(null);
      setDraftDeleteNotice({
        tone: "alert",
        message: "Save or discard unsaved changes before deleting this draft.",
      });
      return;
    }

    setDraftDeleteNotice(null);
    setDraftDeleteBusyId(draft.id);
    try {
      const result = await deleteBuildDraft(
        organization.organizationId,
        draft.id,
        draft.revision,
        { organization_id: organization.organizationId },
      );
      const latest = currentProjectContext();
      if (
        !latest ||
        latest.organizationId !== organization.organizationId ||
        latest.organizationId !== organization.organizationId
      ) {
        return;
      }
      setDraftDeleteIntent(null);
      if (result.id !== draft.id || result.deleted !== true) {
        const refreshed = await refreshBuildLists(organization);
        setDraftDeleteNotice({
          tone: "alert",
          message: refreshed.drafts.status === "loaded"
            ? "The server response did not confirm deletion. Check the refreshed draft list before trying again."
            : "The server response did not confirm deletion, and the draft list could not be refreshed. Check its state before trying again.",
        });
        return;
      }
      if (activeDraft?.id === draft.id) closeDraftEditor();
      const refreshed = await refreshBuildLists(organization);
      setDraftDeleteNotice(
        refreshed.drafts.status === "loaded"
          ? { tone: "status", message: "Draft deleted." }
          : {
              tone: "alert",
              message: "The server accepted draft deletion, but the draft list could not be refreshed. Refresh to confirm its state.",
            },
      );
    } catch (error) {
      setDraftDeleteIntent(null);
      const current = currentProjectContext();
      if (
        !current ||
        current.organizationId !== organization.organizationId ||
        current.organizationId !== organization.organizationId
      ) {
        return;
      }
      if (statusCode(error) === 409) {
        if (activeDraft?.id === draft.id && !hasUnsavedChanges) closeDraftEditor();
        const refreshed = await refreshBuildLists(organization);
        setDraftDeleteNotice({
          tone: "alert",
          message: refreshed.drafts.status === "loaded"
            ? "This draft changed or is no longer available. The latest draft list was refreshed; review it before trying again."
            : "This draft changed or is no longer available, and the latest draft list could not be loaded.",
        });
      } else {
        const refreshed = await refreshBuildLists(organization);
        setDraftDeleteNotice({
          tone: "alert",
          message: refreshed.drafts.status === "loaded"
            ? "Could not confirm whether the draft was deleted. Check the refreshed list before trying again."
            : "Could not confirm whether the draft was deleted, and the draft list could not be refreshed. Check its state before trying again.",
        });
      }
    } finally {
      setDraftDeleteBusyId(null);
    }
  };

  const submitPublicationRequest = async () => {
    const organization = currentProjectContext();
    if (
      !organization ||
      !activeDraft ||
      !canRequestPublication ||
      !selectedFilePath ||
      draftFile.status !== "loaded"
    ) {
      return;
    }

    const draftId = activeDraft.id;
    const revision = activeDraft.revision;
    const filePath = selectedFilePath;
    const knownFile = draftFile.file;
    const requestId = editorRequestId.current;
    setPublicationRequest({ status: "requesting", draftId });
    setFileMutationError(null);
    try {
      const latestFile = await getBuildDraftFile(
        organization.organizationId,
        draftId,
        filePath,
      );
      if (editorRequestId.current !== requestId) return;
      if (
        latestFile.revision !== revision ||
        latestFile.content_hash !== knownFile.content_hash
      ) {
        setDraftValidation({ status: "idle" });
        setDraftFile((current) =>
          current.status === "loaded" ? { ...current, stale: true } : current,
        );
        setFileMutationError(
          "This draft changed after validation. Reload the latest file and validate again.",
        );
        setPublicationRequest({ status: "idle" });
        return;
      }

      const result = await publishBuildDraftDirect(
        organization.organizationId,
        draftId,
      );
      if (editorRequestId.current !== requestId) return;
      setPublicationRequest({ status: "submitted", draftId, value: result });
    } catch {
      if (editorRequestId.current !== requestId) return;
      setPublicationRequest({
        status: "error",
        draftId,
        message: "Could not publish this draft. Please try again.",
      });
    }
  };

  const sendAiSuggestion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const organization = currentProjectContext();
    if (
      aiRequestBusy.current || fileMutationBusy || validationBusy || publicationBusy ||
      !organization || state.status !== "authenticated" || !activeDraft ||
      draftFile.status !== "loaded" || draftFile.stale || selectedFilePath !== draftFile.file.path ||
      aiAccess.status !== "loaded" || !aiAccess.access.access_granted ||
      (aiModelSource === "hosted" ? !aiAccess.access.provider_ready : !aiAccess.access.byo_enabled || !aiConnection || !!aiConnection.revoked_at)
    ) return;

    const prompt = aiPrompt.trim();
    const path = draftFile.file.path;
    const source = draftFile.value;
    const encoder = new TextEncoder();
    if (!prompt) {
      setAiAssistant({ status: "error", message: "Describe the change before requesting a suggestion." });
      return;
    }
    if (encoder.encode(prompt).byteLength > AI_PROMPT_MAX_BYTES) {
      setAiAssistant({ status: "error", message: "Keep the prompt to 8 KiB or less." });
      return;
    }
    if (encoder.encode(path).byteLength > AI_PATH_MAX_BYTES) {
      setAiAssistant({ status: "error", message: "This file path is too long to send for a suggestion." });
      return;
    }
    if (encoder.encode(source).byteLength > AI_FILE_MAX_BYTES) {
      setAiAssistant({ status: "error", message: "Keep the selected file to 32 KiB or less for AI assistance." });
      return;
    }

    const context: AiProposalContext = {
      userId: state.user.id,
      organizationId: organization.organizationId,
      draftId: activeDraft.id,
      editorRequestId: editorRequestId.current,
      path,
      source,
    };
    const sequence = ++aiRequestSequence.current;
    const controller = new AbortController();
    aiRequestController.current = controller;
    aiRequestBusy.current = true;
    aiProposalPending.current = false;
    setAiAssistantNotice(null);
    setAiAssistant({ status: "requesting" });
    try {
      const value = await requestAiAssist({ prompt, file: { path, content: source }, signal: controller.signal,
        ...(aiModelSource === "byo" ? { source: "byo" as const, connection_id: aiConnection!.id } : {}),
      });
      if (sequence !== aiRequestSequence.current || controller.signal.aborted) return;
      const currentProject = currentProjectContext();
      if (
        state.status !== "authenticated" || state.user.id !== context.userId ||
        !currentProject || currentProject.organizationId !== context.organizationId ||
        currentProject.organizationId !== context.organizationId || activeDraft?.id !== context.draftId ||
        editorRequestId.current !== context.editorRequestId || draftFile.status !== "loaded" ||
        draftFile.file.path !== context.path || draftFile.value !== context.source
      ) {
        invalidateAiAssistant("The selected source changed while the request was running. Review the current file and request a new suggestion.");
        return;
      }
      aiProposalPending.current = true;
      setAiAssistant({ status: "proposal", context, value });
    } catch {
      if (sequence !== aiRequestSequence.current || controller.signal.aborted) return;
      setAiAssistant({ status: "error", message: "AI could not return a suggestion. This request was not retried. An unfinished request may still count against your limits; ask an administrator if access remains blocked." });
    } finally {
      if (sequence === aiRequestSequence.current) {
        aiRequestBusy.current = false;
        aiRequestController.current = null;
      }
    }
  };

  const applyAiSuggestion = () => {
    if (aiAssistant.status !== "proposal") return;
    const { context, value } = aiAssistant;
    const organization = currentProjectContext();
    if (
      state.status !== "authenticated" || state.user.id !== context.userId || !organization ||
      organization.organizationId !== context.organizationId || organization.organizationId !== context.organizationId ||
      activeDraft?.id !== context.draftId || editorRequestId.current !== context.editorRequestId ||
      draftFile.status !== "loaded" || draftFile.stale || draftFile.file.path !== context.path ||
      draftFile.value !== context.source
    ) {
      invalidateAiAssistant("The selected source changed. The old suggestion was discarded.");
      return;
    }
    setDraftFile((current) => current.status === "loaded" ? { ...current, value: value.suggestion } : current);
    aiProposalPending.current = false;
    setAiAssistant({ status: "idle" });
    setAiAssistantNotice("Suggestion applied to the editor. Save and validate it separately when you are ready.");
  };

  const validateActiveDraft = async () => {
    const organization = currentProjectContext();
    if (
      !organization ||
      !activeDraft ||
      fileMutationBusy ||
      validationBusy ||
      publicationBusy ||
      hasUnsavedChanges ||
      (draftFile.status === "loaded" && draftFile.stale) ||
      draftFiles?.status !== "loaded"
    ) {
      return;
    }

    const draftId = activeDraft.id;
    const revision = activeDraft.revision;
    const requestId = editorRequestId.current;
    setDraftValidation({ status: "validating", draftId });
    try {
      const report = await validateBuildDraft(
        organization.organizationId,
        draftId,
      );
      if (editorRequestId.current !== requestId) return;
      setDraftValidation({ status: "report", draftId, revision, value: report });
    } catch {
      if (editorRequestId.current !== requestId) return;
      setDraftValidation({
        status: "error",
        draftId,
        message: "Could not validate this draft. Please try again.",
      });
    }
  };

  const organizationName =
    loadState.status === "loaded" ? loadState.organizationName : undefined;

  const repoCommitShort =
    repoFiles?.status === "loaded" ? shortCommit(repoFiles.commit) : null;
  const repoWorkspaceLabel =
    repoWorkspace.status === "ready"
      ? repoCommitShort
        ? `Repository · ${repoWorkspace.repo.repo_name} · ${repoCommitShort}`
        : `Repository · ${repoWorkspace.repo.repo_name}`
      : repoWorkspace.status === "unlinked"
        ? "Repository · not linked"
        : "Repository";

  const authoringSections = loadState.status === "loaded" ? (
    <>
          {draftDeleteNotice ? (
            <p
              className={`text-sm ${draftDeleteNotice.tone === "alert" ? "text-[var(--danger)]" : "text-[var(--muted-foreground)]"}`}
              role={draftDeleteNotice.tone}
            >
              {draftDeleteNotice.message}
            </p>
          ) : null}
          <ExtensionsCuratedPanel
            organizationId={loadState.organizationId}
            publisher={loadState.organizationSlug}
            packages={loadState.packages}
            drafts={loadState.drafts}
            onCreated={() => {
              const organization = currentProjectContext();
              if (organization) void refreshBuildLists(organization);
            }}
          >
            {loadState.packages.status === "loaded" &&
            loadState.packages.items.length > 0 ? (
              <div className="space-y-3">
                {loadState.packages.items.map((extensionPackage) => {
                  const versionKey = packageVersionKey(
                    loadState.organizationId,
                    extensionPackage.id,
                  );
                  const versionState = packageVersions[versionKey] ?? {
                    status: "idle" as const,
                  };
                  const compareSelection = versionCompareSelections[versionKey] ?? {
                    first: "",
                    second: "",
                  };
                  const compareFirst =
                    versionState.status === "loaded"
                      ? versionState.items.find(
                          (version) => version.id === compareSelection.first,
                        )
                      : undefined;
                  const compareSecond =
                    versionState.status === "loaded"
                      ? versionState.items.find(
                          (version) => version.id === compareSelection.second,
                        )
                      : undefined;
                  const repoLink = repoLinks[extensionPackage.id];
                  return (
                    <Card key={`${loadState.organizationId}:${loadState.organizationId}:${extensionPackage.id}:${state.status === "authenticated" ? state.user.id : ""}`}>
                      <CardHeader>
                        <div className="min-w-0">
                          <CardTitle>
                            <CuratedExtensionDetails
                              organizationId={loadState.organizationId}
                              canManage={loadState.organizationRole === "owner"}
                              extensionPackage={extensionPackage}
                              versions={versionState}
                              onLoadVersions={() => void loadPackageVersions(extensionPackage)}
                            />
                          </CardTitle>
                          <CardDescription>
                            {extensionPackage.visibility} · {extensionPackage.pricing}
                          </CardDescription>
                        </div>
                        <Badge tone="neutral">{extensionPackage.status}</Badge>
                      </CardHeader>
                      <CardContent className="space-y-3 pt-3">
                        {DRAFTABLE_PACKAGE_STATUSES.has(extensionPackage.status) ? (
                          <div className="space-y-2">
                            {repoLink?.status === "linked" ? (
                              <p className="text-sm text-[var(--muted-foreground)]">
                                Repository <code className="break-all">{repoLink.repo.repo_name}</code>
                              </p>
                            ) : null}
                            <div className="flex flex-wrap gap-2">
                              {repoLink?.status === "linked" ? (
                                <Button
                                  aria-label={`Open repository for ${extensionPackage.publisher}/${extensionPackage.key}`}
                                  disabled={repoBusy}
                                  onClick={() => requestOpenRepo(extensionPackage)}
                                  size="sm"
                                  variant="outline"
                                >
                                  Edit repository
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    aria-label={`Create draft for ${extensionPackage.publisher}/${extensionPackage.key}`}
                                    disabled={creatingDraftFor !== null}
                                    onClick={() => void startDraft(extensionPackage)}
                                    size="sm"
                                    variant="outline"
                                  >
                                    {creatingDraftFor === extensionPackage.id
                                      ? "Creating draft..."
                                      : "Create draft"}
                                  </Button>
                                  {loadState.organizationRole === "owner" ? (
                                    <Button
                                      aria-label={`Open repository for ${extensionPackage.publisher}/${extensionPackage.key}`}
                                      disabled={repoBusy}
                                      onClick={() => requestOpenRepo(extensionPackage)}
                                      size="sm"
                                      variant="outline"
                                    >
                                      Repository setup
                                    </Button>
                                  ) : null}
                                </>
                              )}
                            </div>
                          </div>
                        ) : null}
                        <div className="space-y-3">
                          <Button
                            aria-label={`View versions for ${extensionPackage.publisher}/${extensionPackage.key}`}
                            disabled={versionState.status === "loading"}
                            onClick={() => void loadPackageVersions(extensionPackage)}
                            size="sm"
                            variant="outline"
                          >
                            {versionState.status === "loading"
                              ? "Loading versions..."
                              : versionState.status === "loaded"
                                ? "Refresh versions"
                                : versionState.status === "error"
                                  ? "Retry versions"
                                  : "View versions"}
                          </Button>
                          {versionState.status === "loading" ? (
                            <p role="status" className="text-sm text-[var(--muted-foreground)]">
                              Loading recorded package versions...
                            </p>
                          ) : null}
                          {versionState.status === "error" ? (
                            <p role="alert" className="text-sm text-[var(--danger)]">
                              {versionState.message}
                            </p>
                          ) : null}
                          {versionState.status === "loaded" && versionState.items.length === 0 ? (
                            <p role="status" className="text-sm text-[var(--muted-foreground)]">
                              No recorded versions for this package.
                            </p>
                          ) : null}
                          {versionState.status === "loaded" && versionState.items.length > 0 ? (
                            <div className="space-y-3">
                              <h3 className="font-medium">Recorded versions</h3>
                              <ol className="space-y-3">
                                {versionState.items.map((version) => (
                                  <li
                                    className="space-y-2 rounded-md border border-[var(--border)] p-3 text-sm"
                                    key={version.id}
                                  >
                                    <p>
                                      Version <code>{version.semver}</code> · state: <code>{version.state}</code>
                                    </p>
                                    <p>API version: <code>{version.api_version}</code></p>
                                    <p className="break-all">Bundle digest: <code>{version.bundle_digest}</code></p>
                                    {version.state === "published" && installationTargetApps(version.manifest).length !== 0 ? (
                                      loadState.organizationRole === "owner" ? (
                                        <DirectBuildInstallation
                                          key={`${loadState.organizationId}:${loadState.organizationId}:${extensionPackage.id}:${version.id}:${state.status === "authenticated" ? state.user.id : ""}`}
                                          organizationId={loadState.organizationId}
                                          userId={state.status === "authenticated" ? state.user.id : ""}
                                          packageId={extensionPackage.id}
                                          publisher={extensionPackage.publisher}
                                          packageKey={extensionPackage.key}
                                          version={version.semver}
                                          targetApps={installationTargetApps(version.manifest)}
                                          permissions={installationCapabilities(version.manifest)}
                                          onInstalled={() => {
                                            const organization = currentProjectContext();
                                            if (organization) void refreshInstallations(organization);
                                          }}
                                        />
                                      ) : (
                                        <p className="text-sm text-[var(--muted-foreground)]">
                                          Only the organization owner can install extensions.
                                        </p>
                                      )
                                    ) : null}
                                    <details>
                                      <summary className="cursor-pointer">Manifest (inert text)</summary>
                                      <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-[var(--muted)] p-3 text-xs">
                                        {formatInertJson(version.manifest)}
                                      </pre>
                                    </details>
                                  </li>
                                ))}
                              </ol>
                              {versionState.items.length >= 2 ? (
                                <div className="space-y-3 rounded-xl border border-[var(--border)] p-4">
                                  <h4 className="font-medium">
                                    Compare recorded versions
                                  </h4>
                                  <div className="grid gap-3 sm:grid-cols-2">
                                    <label className="grid gap-1 text-sm">
                                      <span>First version to compare</span>
                                      <Select
                                        aria-label="First version to compare"
                                        options={versionState.items.map(
                                          (version) => ({
                                            value: version.id,
                                            label: `${version.semver} (${version.state})`,
                                          }),
                                        )}
                                        onChange={(value) =>
                                          updateVersionCompareSelection(
                                            versionKey,
                                            "first",
                                            value,
                                          )
                                        }
                                        placeholder="Choose first version"
                                        value={compareSelection.first}
                                      />
                                    </label>
                                    <label className="grid gap-1 text-sm">
                                      <span>Second version to compare</span>
                                      <Select
                                        aria-label="Second version to compare"
                                        options={versionState.items.map(
                                          (version) => ({
                                            value: version.id,
                                            label: `${version.semver} (${version.state})`,
                                          }),
                                        )}
                                        onChange={(value) =>
                                          updateVersionCompareSelection(
                                            versionKey,
                                            "second",
                                            value,
                                          )
                                        }
                                        placeholder="Choose second version"
                                        value={compareSelection.second}
                                      />
                                    </label>
                                  </div>
                                  {compareSelection.first &&
                                  compareSelection.first === compareSelection.second ? (
                                    <p
                                      className="text-sm text-[var(--muted-foreground)]"
                                      role="status"
                                    >
                                      Choose two different recorded versions to
                                      compare.
                                    </p>
                                  ) : null}
                                  {compareFirst &&
                                  compareSecond &&
                                  compareFirst.id !== compareSecond.id ? (
                                    <section
                                      aria-label="Manifest and capability metadata comparison"
                                      className="space-y-3"
                                    >
                                      <div>
                                        <h5 className="font-medium">
                                          Manifest and capability metadata
                                          comparison
                                        </h5>
                                        <p className="text-xs text-[var(--muted-foreground)]">
                                          Compares recorded manifest JSON and
                                          version metadata only. This is not a
                                          source-file diff.
                                        </p>
                                      </div>
                                      <div className="grid gap-3 lg:grid-cols-2">
                                        {[
                                          {
                                            label: "First version",
                                            version: compareFirst,
                                          },
                                          {
                                            label: "Second version",
                                            version: compareSecond,
                                          },
                                        ].map(({ label, version }) => {
                                          const selectedVersion = version;
                                          return (
                                            <Card key={selectedVersion.id}>
                                              <CardHeader>
                                                <div>
                                                  <CardTitle>
                                                    {label}: {selectedVersion.semver}
                                                  </CardTitle>
                                                  <CardDescription>
                                                    State: {selectedVersion.state}
                                                  </CardDescription>
                                                </div>
                                              </CardHeader>
                                              <CardContent className="space-y-3 pt-0 text-sm">
                                                <p>
                                                  API version: {selectedVersion.api_version}
                                                </p>
                                                <p className="break-all">
                                                  Bundle digest: {selectedVersion.bundle_digest}
                                                </p>
                                                <div>
                                                  <p className="mb-1 text-xs text-[var(--muted-foreground)]">
                                                    Manifest JSON, including any
                                                    declared capability metadata
                                                  </p>
                                                  <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-[var(--muted)] p-3 text-xs">
                                                    {formatInertJson(
                                                      selectedVersion.manifest,
                                                    )}
                                                  </pre>
                                                </div>
                                              </CardContent>
                                            </Card>
                                          );
                                        })}
                                      </div>
                                    </section>
                                  ) : null}
                                </div>
                              ) : (
                                <p
                                  className="text-sm text-[var(--muted-foreground)]"
                                  role="status"
                                >
                                  Record at least two versions to compare their
                                  manifests and metadata.
                                </p>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : null}

          <section aria-labelledby="drafts-heading" className="space-y-4">
            <h2 id="drafts-heading" className="text-lg font-semibold">
              Drafts
            </h2>
            {loadState.drafts.status === "loading" ? (
              <p role="status" className="text-sm text-[var(--muted-foreground)]">
                Loading drafts...
              </p>
            ) : null}
            {loadState.drafts.status === "error" ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {loadState.drafts.message}
              </p>
            ) : null}
            {loadState.drafts.status === "loaded" &&
            loadState.drafts.items.length === 0 ? (
              <EmptyState
                title="No drafts yet"
                reason="Create a draft from one of this organization’s packages."
              />
            ) : null}
            {loadState.drafts.status === "loaded" &&
            loadState.drafts.items.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {loadState.drafts.items.map((draft) => {
                  const displayName = `${draft.package.publisher}/${draft.package.key}`;
                  const hasDraftUnsavedChanges =
                    activeDraft?.id === draft.id && hasUnsavedChanges;
                  const deleteBusy = draftDeleteBusyId === draft.id;
                  return (
                    <Card key={draft.id}>
                      <CardHeader>
                        <div>
                          <CardTitle>{displayName}</CardTitle>
                          <CardDescription>Revision {draft.revision}</CardDescription>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3 pt-3">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            aria-label={`Open draft editor for ${displayName}`}
                            disabled={fileMutationBusy || validationBusy || publicationBusy || deleteBusy}
                            onClick={() => requestOpenDraft(draft)}
                            size="sm"
                            variant="outline"
                          >
                            Edit files
                          </Button>
                          <Button
                            aria-label={`Delete draft ${displayName}`}
                            disabled={
                              hasDraftUnsavedChanges ||
                              fileMutationBusy ||
                              validationBusy ||
                              publicationBusy ||
                              draftDeleteBusyId !== null
                            }
                            onClick={() => requestDraftDeletion(draft)}
                            size="sm"
                            variant="destructive"
                          >
                            {draftDeleteBusyId === draft.id ? "Deleting..." : "Delete draft"}
                          </Button>
                        </div>
                        {hasDraftUnsavedChanges ? (
                          <p className="text-sm text-[var(--muted-foreground)]" role="status">
                            Save or discard unsaved changes before deleting this draft.
                          </p>
                        ) : null}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : null}

            <Dialog
              description="Draft files will be deleted with it. The server refuses deletion while a publication request is pending; refresh and try again if that happens."
              onOpenChange={(open) => {
                if (!open && draftDeleteBusyId === null) setDraftDeleteIntent(null);
              }}
              open={draftDeleteIntent !== null}
              title={draftDeleteIntent
                ? `Delete ${draftDeleteIntent.package.publisher}/${draftDeleteIntent.package.key}?`
                : "Delete extension draft?"}
            >
              <div className="flex justify-end gap-2">
                <Button
                  disabled={draftDeleteBusyId !== null}
                  onClick={() => setDraftDeleteIntent(null)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  aria-label="Confirm draft deletion"
                  disabled={draftDeleteBusyId !== null}
                  onClick={() => void confirmDraftDeletion()}
                  variant="destructive"
                >
                  {draftDeleteBusyId !== null ? "Checking and deleting..." : "Delete draft"}
                </Button>
              </div>
            </Dialog>
          </section>

          {activeDraft ? (
            <section aria-label="Draft workspace">
              <EditorWorkspace
                title={`${activeDraft.package.publisher}/${activeDraft.package.key}`}
                onClose={() => { if (!fileMutationBusy && !validationBusy && !publicationBusy) requestCloseDraftEditor(); }}
                onManifest={draftFiles?.status === "loaded" && draftFiles.items.some(file => file.path === "manifest.json")
                  ? () => requestSelectFile("manifest.json") : undefined}
              >
                <WorkspaceDiagnostics>
                  <section aria-labelledby="draft-validation-heading" className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 id="draft-validation-heading" className="font-medium">
                          Draft validation
                        </h3>
                        <p className="text-sm text-[var(--muted-foreground)]">
                          Validation checks the saved manifest and bundle structure and, when UI views are present, validates those views against the canonical UI schema. It never executes the extension.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          aria-label="Validate draft"
                          disabled={
                            validationBusy ||
                            publicationBusy ||
                            fileMutationBusy ||
                            hasUnsavedChanges ||
                            (draftFile.status === "loaded" && draftFile.stale) ||
                            draftFiles?.status !== "loaded"
                          }
                          onClick={() => void validateActiveDraft()}
                          size="sm"
                          variant="outline"
                        >
                          {validationBusy ? "Validating draft..." : "Validate draft"}
                        </Button>
                        <Button
                          aria-label="Publish draft"
                          disabled={!canRequestPublication}
                          onClick={() => void submitPublicationRequest()}
                          size="sm"
                        >
                          {publicationBusy
                            ? "Publishing..."
                            : publicationAlreadyRequested
                              ? "Published"
                              : "Publish draft"}
                        </Button>
                      </div>
                    </div>
                    {hasUnsavedChanges ? (
                      <p role="status" className="text-sm text-[var(--muted-foreground)]">
                        Save your changes before validating.
                      </p>
                    ) : null}
                    {draftFile.status === "loaded" && draftFile.stale ? (
                      <p role="status" className="text-sm text-[var(--muted-foreground)]">
                        Reload the latest file and validate again before publishing.
                      </p>
                    ) : null}
                    {validationBusy ? (
                      <p role="status" className="text-sm text-[var(--muted-foreground)]">
                        Validating draft...
                      </p>
                    ) : null}
                    {draftValidation.status === "error" &&
                    draftValidation.draftId === activeDraft.id ? (
                      <p role="alert" className="text-sm text-[var(--danger)]">
                        {draftValidation.message}
                      </p>
                    ) : null}
                    {draftValidation.status === "report" &&
                    draftValidation.draftId === activeDraft.id ? (
                      <Card aria-label="Validation report">
                        <CardHeader>
                          <CardTitle>Validation report</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <p className="text-sm text-[var(--muted-foreground)]">
                            The API-reported UI schema validation status is shown below. TypeScript checks are not run, and runtime validation remains pending.
                          </p>
                          <p><code>valid: {String(draftValidation.value.valid)}</code></p>
                          <dl className="grid gap-2 text-sm sm:grid-cols-3">
                            {draftValidation.value.typescript_validation !== undefined ? (
                              <div>
                                <dt>TypeScript validation:</dt>
                                <dd><code>{draftValidation.value.typescript_validation}</code></dd>
                              </div>
                            ) : null}
                            {draftValidation.value.ui_schema_validation !== undefined ? (
                              <div>
                                <dt>UI schema validation:</dt>
                                <dd><code>{draftValidation.value.ui_schema_validation}</code></dd>
                              </div>
                            ) : null}
                            <div>
                              <dt>Runtime validation:</dt>
                              <dd><code>{draftValidation.value.runtime_validation}</code></dd>
                            </div>
                          </dl>
                          <div>
                            <h4 className="font-medium">Problems</h4>
                            {draftValidation.value.problems.length === 0 ? (
                              <p className="text-sm text-[var(--muted-foreground)]">
                                No validation problems reported.
                              </p>
                            ) : (
                              <ul className="mt-2 space-y-3">
                                {draftValidation.value.problems.map((problem, index) => {
                                  const documentationUrl =
                                    resolveExtensionDocsUrl(problem.docs_url);
                                  return (
                                    <li
                                      className="rounded-md border border-[var(--border)] p-3"
                                      key={`${problem.code}:${problem.path}:${problem.line}:${index}`}
                                    >
                                      <p className="text-sm">
                                        <code>{problem.code}</code> · <code>{problem.path}</code> · line {problem.line}
                                      </p>
                                      <p className="mt-1 text-sm">{problem.message}</p>
                                      {documentationUrl ? (
                                        <a
                                          aria-label="Validation documentation"
                                          className="mt-2 inline-block text-sm underline"
                                          href={documentationUrl}
                                          rel="noreferrer"
                                          target="_blank"
                                        >
                                          Documentation
                                        </a>
                                      ) : null}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ) : null}
                    {draftValidation.status === "report" &&
                    draftValidation.draftId === activeDraft.id &&
                    !currentValidation ? (
                      <p role="status" className="text-sm text-[var(--muted-foreground)]">
                        This validation report is no longer current for this draft revision. Validate again before publishing.
                      </p>
                    ) : null}
                    {currentValidation && !draftValidation.value.valid ? (
                      <p role="status" className="text-sm text-[var(--muted-foreground)]">
                        Resolve the reported problems and validate again before publishing.
                      </p>
                    ) : null}
                    {publicationRequest.status === "error" &&
                    publicationRequest.draftId === activeDraft.id ? (
                      <p role="alert" className="text-sm text-[var(--danger)]">
                        {publicationRequest.message}
                      </p>
                    ) : null}
                    {publicationRequest.status === "submitted" &&
                    publicationRequest.draftId === activeDraft.id ? (
                      <div role="status" className="space-y-2 rounded-md border border-[var(--border)] p-3 text-sm">
                        <p>
                          Publication status: <code>{publicationRequest.value.status}</code>.
                        </p>
                        <p>Request ID: <code>{publicationRequest.value.id}</code></p>
                        <p>Action: <code>{publicationRequest.value.action}</code></p>
                        <details>
                          <summary className="cursor-pointer">Publication request summary (inert text)</summary>
                          <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-[var(--muted)] p-3 text-xs">
                            {formatInertJson(publicationRequest.value.summary)}
                          </pre>
                        </details>
                      </div>
                    ) : null}
                  </section>
                </WorkspaceDiagnostics>
                    <WorkspaceExplorer>
                      <form
                        aria-label="Create draft file"
                        className="grid gap-2"
                        onSubmit={(event) => void createDraftFile(event)}
                      >
                        <label className="grid gap-1.5 text-sm font-medium">
                          New file path
                          <Input
                            aria-label="New file path"
                            autoComplete="off"
                            onChange={(event) => setNewFilePath(event.target.value)}
                            placeholder="src/main.ts"
                            value={newFilePath}
                          />
                        </label>
                        <Button
                          disabled={
                            fileMutationBusy ||
                            validationBusy ||
                            publicationBusy ||
                            draftFiles?.status !== "loaded" ||
                            !newFilePath.trim() ||
                            (draftFile.status === "loaded" && draftFile.stale)
                          }
                          size="sm"
                          type="submit"
                          variant="outline"
                        >
                          Create file
                        </Button>
                      </form>
                      {draftFiles?.status === "loading" ? (
                        <p role="status" className="text-sm text-[var(--muted-foreground)]">
                          Loading draft files...
                        </p>
                      ) : null}
                      {draftFiles?.status === "error" ? (
                        <p role="alert" className="text-sm text-[var(--danger)]">
                          {draftFiles.message}
                        </p>
                      ) : null}
                      {draftFiles?.status === "loaded" ? (
                        <FileExplorer
                          ariaLabel="Draft files"
                          emptyMessage="No files in this draft yet."
                          files={draftFiles.items}
                          onSelect={requestSelectFile}
                          selectedPath={selectedFilePath}
                        />
                      ) : null}
                    </WorkspaceExplorer>

                    <WorkspaceEditor>
                      {fileMutationError ? <p role="alert" className="text-sm text-[var(--danger)]">{fileMutationError}</p> : null}
                      {fileSavedNotice ? <p role="status" className="text-sm text-[var(--muted-foreground)]">{fileSavedNotice}</p> : null}
                      <p className="text-xs text-[var(--muted-foreground)]">{selectedFilePath ?? "Select a file"} · Revision {activeDraft.revision}{hasUnsavedChanges ? " · Unsaved" : ""}</p>
                      {draftFile.status === "idle" ? (
                        <p className="text-sm text-[var(--muted-foreground)]">
                          Choose a file to view or edit its source.
                        </p>
                      ) : null}
                      {draftFile.status === "loading" ? (
                        <p role="status" className="text-sm text-[var(--muted-foreground)]">
                          Loading file source...
                        </p>
                      ) : null}
                      {draftFile.status === "error" ? (
                        <p role="alert" className="text-sm text-[var(--danger)]">
                          {draftFile.message}
                        </p>
                      ) : null}
                      {draftFile.status === "loaded" ? (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              aria-label="Save draft file"
                              disabled={
                                fileMutationBusy ||
                                validationBusy ||
                                publicationBusy ||
                                draftFile.stale ||
                                !hasUnsavedChanges
                              }
                              onClick={() => void saveDraftFile()}
                              size="sm"
                            >
                              {fileMutationBusy ? "Saving..." : "Save file"}
                            </Button>
                            <Button
                              aria-label="Delete draft file"
                              disabled={
                                fileMutationBusy ||
                                validationBusy ||
                                publicationBusy ||
                                draftFile.stale ||
                                hasUnsavedChanges
                              }
                              onClick={() => setDeleteConfirmationOpen(true)}
                              size="sm"
                              variant="destructive"
                            >
                              Delete file
                            </Button>
                            {draftFile.stale ? (
                              <Button
                                aria-label="Reload current file"
                                disabled={fileMutationBusy || validationBusy || publicationBusy}
                                onClick={reloadCurrentFile}
                                size="sm"
                                variant="outline"
                              >
                                Reload latest version
                              </Button>
                            ) : null}
                          </div>
                          <CodeEditor
                            ariaLabel={`Editor for ${draftFile.file.path}`}
                            height="100%"
                            theme="vs-dark"
                            language={languageForFilePath(draftFile.file.path)}
                            loadingMessage="Loading source editor..."
                            onChange={(value) => {
                              invalidateAiAssistant("The selected source changed. The previous suggestion was discarded.");
                              setDraftFile((current) =>
                                current.status === "loaded" && !current.stale
                                  ? { ...current, value }
                                  : current,
                              );
                              setFileSavedNotice(null);
                              setFileMutationError(null);
                              setDraftValidation({ status: "idle" });
                            }}
                            readOnly={fileMutationBusy || validationBusy || publicationBusy || draftFile.stale}
                            value={draftFile.value}
                          />
                        </>
                      ) : null}
                    </WorkspaceEditor>
                    <WorkspaceAssistant>
                      {draftFile.status === "loaded" ? (
                          <Card aria-label="AI scripting assistant">
                            <CardHeader>
                              <div>
                              <CardTitle>Sylvia · coding assistant</CardTitle>
                              <CardDescription>
                                AI needs review. Suggestions are never executed, saved, or published automatically.
                              </CardDescription>
                              </div>
                            </CardHeader>
                            <CardContent className="grid gap-3">
                              <PersonalAiConnections key={aiContextUserId ?? "signed-out"} source={aiModelSource} selected={aiConnection}
                                onSourceChange={source => { invalidateAiAssistant("The model source changed. The previous request or proposal was discarded. An in-flight request may still count against your limits."); setAiModelSource(source); }}
                                onSelect={connection => { invalidateAiAssistant("The model connection changed. The previous request or proposal was discarded. An in-flight request may still count against your limits."); setAiConnection(connection); }} />
                              {aiAssistantNotice ? <p role="status" className="text-sm">{aiAssistantNotice}</p> : null}
                              {aiAccess.status === "loading" ? (
                                <p role="status" className="text-sm text-[var(--muted-foreground)]">
                                  Checking AI access…
                                </p>
                              ) : aiAccess.status === "error" ? (
                                <p role="status" className="text-sm text-[var(--muted-foreground)]">
                                  AI scripting assistance is unavailable because access could not be checked. No prompt was sent.
                                </p>
                              ) : !aiAccess.access.access_granted ? (
                                <p role="status" className="text-sm text-[var(--muted-foreground)]">
                                  AI scripting assistance is unavailable. Ask a platform administrator for finite AI testing access.
                                </p>
                              ) : (aiModelSource === "hosted" ? !aiAccess.access.provider_ready : !aiAccess.access.byo_enabled || !aiConnection || !!aiConnection.revoked_at) ? (
                                <p role="status" className="text-sm text-[var(--muted-foreground)]">
                                  {aiModelSource === "byo" ? !aiAccess.access.byo_enabled ? "BYO inference is disabled on this server. No prompt was sent." : "Choose an active personal model connection. No prompt was sent." : "Your AI testing grant is active, but AI inference is not ready. No prompt was sent."}
                                </p>
                              ) : (
                                <>
                                  <p className="text-sm text-[var(--muted-foreground)]">
                                    Only your prompt and this selected file’s path and current contents will be sent to {aiModelSource === "byo" ? `your ${aiConnection?.provider} model through SYLVIQ` : "SYLVIQ AI"}. Remove secrets and customer data first. Other files and organization data are not included.
                                  </p>
                                  {aiAccess.access.grant ? (
                                    <p className="text-xs text-[var(--muted-foreground)]">
                                      Grant limit: {aiAccess.access.grant.requests_per_utc_day} requests and {aiAccess.access.grant.tokens_per_utc_day.toLocaleString()} tokens per UTC day; maximum {aiAccess.access.grant.max_concurrency} in progress. {aiModelSource === "byo" ? "BYO does not consume the hosted pool; your provider may bill your own account." : "Requests may also stop when shared testing capacity is reached."}
                                    </p>
                                  ) : null}
                                  <form className="grid gap-3" onSubmit={(event) => void sendAiSuggestion(event)}>
                                    <label className="grid gap-1 text-sm" htmlFor="build-ai-prompt">
                                      Describe the change
                                      <Input
                                        id="build-ai-prompt"
                                        aria-label="Describe the change"
                                        autoComplete="off"
                                        maxLength={AI_PROMPT_MAX_BYTES}
                                        onChange={(event) => { invalidateAiAssistant("The prompt changed. The previous request or proposal was discarded. An in-flight request may still count against your limits."); setAiPrompt(event.target.value); }}
                                        value={aiPrompt}
                                      />
                                    </label>
                                    <p className="text-xs text-[var(--muted-foreground)]">
                                      Selected file: <code>{draftFile.file.path}</code>. Prompt limit 8 KiB; file limit 32 KiB.
                                    </p>
                                    <div>
                                      <Button
                                        aria-label="Send prompt and selected file to AI"
                                        disabled={aiAssistant.status === "requesting" || fileMutationBusy || validationBusy || publicationBusy || draftFile.stale || aiPrompt.trim().length === 0}
                                        type="submit"
                                      >
                                        {aiAssistant.status === "requesting" ? "Waiting for suggestion…" : "Request suggestion"}
                                      </Button>
                                    </div>
                                  </form>
                                  {aiAssistant.status === "error" ? <p role="alert" className="text-sm text-[var(--danger)]">{aiAssistant.message}</p> : null}
                                  {aiAssistant.status === "proposal" ? (
                                    <div className="grid gap-3">
                                      <p className="text-sm font-medium">Original source sent for review</p>
                                      <CodeEditor
                                        ariaLabel="Original source for AI review"
                                        language={languageForFilePath(aiAssistant.context.path)}
                                        onChange={() => undefined}
                                        readOnly
                                        value={aiAssistant.context.source}
                                      />
                                      <p className="text-sm font-medium">Proposed source — review it before applying.</p>
                                      <CodeEditor
                                        ariaLabel="AI proposed source"
                                        language={languageForFilePath(draftFile.file.path)}
                                        onChange={() => undefined}
                                        readOnly
                                        value={aiAssistant.value.suggestion}
                                      />
                                      <div>
                                        <Button
                                          aria-label="Apply AI proposal to editor"
                                          disabled={fileMutationBusy || validationBusy || publicationBusy || draftFile.stale}
                                          onClick={applyAiSuggestion}
                                          type="button"
                                        >
                                          Apply to editor
                                        </Button>
                                      </div>
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </CardContent>
                          </Card>
                      ) : null}
                    </WorkspaceAssistant>
              </EditorWorkspace>

              <Dialog
                description="This removes the file from this draft. This change cannot be undone."
                onOpenChange={(open) => {
                  if (!fileMutationBusy && !publicationBusy) setDeleteConfirmationOpen(open);
                }}
                open={deleteConfirmationOpen}
                title="Delete this draft file?"
              >
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => setDeleteConfirmationOpen(false)}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={fileMutationBusy || validationBusy || publicationBusy}
                    onClick={() => void deleteDraftFile()}
                    variant="destructive"
                  >
                    Delete file
                  </Button>
                </div>
              </Dialog>

              <Dialog
                description="Your unsaved source changes will be discarded."
                onOpenChange={(open) => {
                  if (!open) setDiscardIntent(null);
                }}
                open={discardIntent !== null}
                title="Discard unsaved changes?"
              >
                <div className="flex justify-end gap-2">
                  <Button onClick={() => setDiscardIntent(null)} variant="outline">
                    Keep editing
                  </Button>
                  <Button onClick={discardAndContinue} variant="destructive">
                    Discard changes
                  </Button>
                </div>
              </Dialog>
            </section>
          ) : null}
          {activeRepoPackage ? (
            <section aria-label="Repository workspace">
              <EditorWorkspace
                assistant={false}
                modeLabel={repoWorkspaceLabel}
                onClose={() => requestCloseRepoWorkspace()}
                title={`${activeRepoPackage.publisher}/${activeRepoPackage.key}`}
              >
                <WorkspaceExplorer>
                  {repoWorkspace.status === "checking" ? (
                    <p role="status" className="text-sm text-[var(--muted-foreground)]">
                      Checking the repository...
                    </p>
                  ) : null}
                  {repoWorkspace.status === "error" ? (
                    <p role="alert" className="text-sm text-[var(--danger)]">
                      {repoWorkspace.message}
                    </p>
                  ) : null}
                  {repoWorkspace.status === "unlinked" ? (
                    <p className="text-sm text-[var(--muted-foreground)]">
                      No repository files yet.
                    </p>
                  ) : null}
                  {repoWorkspace.status === "ready" ? (
                    <>
                      <form
                        aria-label="Create repository file"
                        className="grid gap-2"
                        onSubmit={(event) => void createRepoFile(event)}
                      >
                        <label className="grid gap-1.5 text-sm font-medium">
                          New file path
                          <Input
                            aria-label="New repository file path"
                            autoComplete="off"
                            onChange={(event) => setRepoNewFilePath(event.target.value)}
                            placeholder="src/main.ts"
                            value={repoNewFilePath}
                          />
                        </label>
                        <Button
                          disabled={
                            repoMutationBusy ||
                            repoBuildBusy ||
                            repoFiles?.status !== "loaded" ||
                            !repoNewFilePath.trim()
                          }
                          size="sm"
                          type="submit"
                          variant="outline"
                        >
                          Create file
                        </Button>
                      </form>
                      {repoFiles?.status === "loading" ? (
                        <p role="status" className="text-sm text-[var(--muted-foreground)]">
                          Loading repository files...
                        </p>
                      ) : null}
                      {repoFiles?.status === "error" ? (
                        <p role="alert" className="text-sm text-[var(--danger)]">
                          {repoFiles.message}
                        </p>
                      ) : null}
                      {repoFiles?.status === "loaded" ? (
                        <RepoFileTree
                          ariaLabel="Repository files"
                          files={repoFiles.files}
                          onSelect={requestSelectRepoFile}
                          selectedPath={repoSelectedPath}
                        />
                      ) : null}
                    </>
                  ) : null}
                </WorkspaceExplorer>
                <WorkspaceEditor>
                  {repoWorkspace.status === "checking" ? (
                    <p role="status" className="text-sm text-[var(--muted-foreground)]">
                      Checking the repository...
                    </p>
                  ) : null}
                  {repoWorkspace.status === "error" ? (
                    <p role="alert" className="text-sm text-[var(--danger)]">
                      {repoWorkspace.message}
                    </p>
                  ) : null}
                  {repoWorkspace.status === "unlinked" ? (
                    <div className="grid gap-3">
                      <div className="space-y-1">
                        <h3 className="font-medium">Platform git repository</h3>
                        <p className="text-sm text-[var(--muted-foreground)]">
                          The code for this package lives in the platform git repository. Edit it here or with any IDE on your computer; every save is recorded in the repository's history.
                        </p>
                      </div>
                      <p className="text-sm">
                        Repository name: <code>{activeRepoPackage.publisher}.{activeRepoPackage.key}</code>
                      </p>
                      {repoMutationError ? (
                        <p role="alert" className="text-sm text-[var(--danger)]">
                          {repoMutationError}
                        </p>
                      ) : null}
                      {loadState.organizationRole === "owner" ? (
                        <div>
                          <Button
                            aria-label="Link repository"
                            disabled={repoLinkBusy}
                            onClick={() => void linkRepoWorkspace()}
                            size="sm"
                          >
                            {repoLinkBusy ? "Linking..." : "Link repository"}
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-[var(--muted-foreground)]">
                          Ask an organization owner to link a repository for this package. Drafts remain available for editing in the meantime.
                        </p>
                      )}
                    </div>
                  ) : null}
                  {repoWorkspace.status === "ready" ? (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm">
                          <span className="font-medium">{repoWorkspace.repo.repo_name}</span>
                          {" · "}Commit <code>{repoCommitShort ?? "unavailable"}</code>
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            aria-label="Build distribution"
                            disabled={repoBuildBusy || repoMutationBusy || repoHasUnsavedChanges}
                            onClick={() => void buildRepoDistribution()}
                            size="sm"
                            variant="outline"
                          >
                            {repoBuildBusy ? "Building..." : "Build distribution"}
                          </Button>
                          <Button
                            aria-label="Release repository"
                            disabled={releaseBusy || repoBuildBusy || repoMutationBusy}
                            onClick={() => {
                              setReleaseError(null);
                              setReleasePendingApprovalId(null);
                              setReleaseDialogOpen(true);
                            }}
                            size="sm"
                          >
                            Release
                          </Button>
                        </div>
                      </div>
                      {repoMutationError ? (
                        <p role="alert" className="text-sm text-[var(--danger)]">
                          {repoMutationError}
                        </p>
                      ) : null}
                      {repoNotice ? (
                        <p role="status" className="text-sm text-[var(--muted-foreground)]">
                          {repoNotice}
                        </p>
                      ) : null}
                      <p className="text-xs text-[var(--muted-foreground)]">
                        {repoSelectedPath ?? "Select a file"}
                        {repoHasUnsavedChanges ? " · Unsaved" : ""}
                      </p>
                      {repoHasUnsavedChanges ? (
                        <p role="status" className="text-sm text-[var(--muted-foreground)]">
                          Commit your changes before building the distribution.
                        </p>
                      ) : null}
                      {repoFile.status === "idle" ? (
                        <p className="text-sm text-[var(--muted-foreground)]">
                          Choose a file to view or edit its source.
                        </p>
                      ) : null}
                      {repoFile.status === "loading" ? (
                        <p role="status" className="text-sm text-[var(--muted-foreground)]">
                          Loading file source...
                        </p>
                      ) : null}
                      {repoFile.status === "error" ? (
                        <p role="alert" className="text-sm text-[var(--danger)]">
                          {repoFile.message}
                        </p>
                      ) : null}
                      {repoFile.status === "inert" ? (
                        <div className="grid gap-2 rounded-md border border-[var(--border)] p-3 text-sm">
                          <p className="font-medium">This file cannot be edited here.</p>
                          <p>
                            <code>{repoFile.path}</code> is not a text file.
                          </p>
                          <p className="text-[var(--muted-foreground)]">
                            Size: {repoFile.size === null ? "unavailable" : `${repoFile.size} bytes`}. Edit it with an IDE on your computer and commit the change to the repository.
                          </p>
                        </div>
                      ) : null}
                      {repoFile.status === "loaded" ? (
                        <>
                          <div className="flex flex-wrap items-end gap-2">
                            <label className="grid gap-1.5 text-sm font-medium">
                              Commit message
                              <Input
                                aria-label="Commit message"
                                autoComplete="off"
                                maxLength={200}
                                onChange={(event) => setRepoCommitMessage(event.target.value)}
                                value={repoCommitMessage}
                              />
                            </label>
                            <Button
                              aria-label="Commit changes"
                              disabled={repoMutationBusy || repoBuildBusy || !repoHasUnsavedChanges}
                              onClick={() => void commitRepoFile()}
                              size="sm"
                            >
                              {repoMutationBusy ? "Committing..." : "Commit changes"}
                            </Button>
                            <Button
                              aria-label="Delete repository file"
                              disabled
                              size="sm"
                              variant="destructive"
                            >
                              Delete file
                            </Button>
                          </div>
                          <p className="text-xs text-[var(--muted-foreground)]">
                            Deleting repository files is not available yet.
                          </p>
                          <CodeEditor
                            ariaLabel={`Editor for ${repoFile.path}`}
                            height="100%"
                            theme="vs-dark"
                            language={languageForFilePath(repoFile.path)}
                            loadingMessage="Loading source editor..."
                            onChange={(value) => {
                              setRepoFile((current) =>
                                current.status === "loaded" && current.path === repoFile.path
                                  ? { ...current, value }
                                  : current,
                              );
                              setRepoNotice(null);
                              setRepoMutationError(null);
                            }}
                            readOnly={repoMutationBusy || repoBuildBusy}
                            value={repoFile.value}
                          />
                        </>
                      ) : null}
                    </>
                  ) : null}
                </WorkspaceEditor>
              </EditorWorkspace>

              <Dialog
                description="A release builds the repository into a new version. First-party packages can stay pending platform owner review before the version goes live."
                onOpenChange={(open) => {
                  if (!open && !releaseBusy) closeRepoReleaseDialog();
                }}
                open={releaseDialogOpen}
                title={`Release ${activeRepoPackage.publisher}/${activeRepoPackage.key}`}
              >
                {releasePendingApprovalId ? (
                  <div className="grid gap-3">
                    <p role="status" className="text-sm">
                      Release {releaseTag.trim()} is pending platform owner review.
                    </p>
                    <p className="text-sm text-[var(--muted-foreground)]">
                      A platform owner reviews first-party releases in Admin. Reference{" "}
                      <code>{releasePendingApprovalId}</code>.
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button onClick={closeRepoReleaseDialog} variant="outline">
                        Close
                      </Button>
                    </div>
                  </div>
                ) : (
                  <form
                    aria-label="Release repository"
                    className="grid gap-3"
                    onSubmit={(event) => void submitRepoRelease(event)}
                  >
                    <label className="grid gap-1.5 text-sm font-medium">
                      Version
                      <Input
                        aria-label="Release version"
                        autoComplete="off"
                        onChange={(event) => {
                          setReleaseTag(event.target.value);
                          setReleaseError(null);
                        }}
                        placeholder="v1.2.3"
                        value={releaseTag}
                      />
                    </label>
                    {releaseError ? (
                      <p role="alert" className="text-sm text-[var(--danger)]">
                        {releaseError}
                      </p>
                    ) : null}
                    <div className="flex justify-end gap-2">
                      <Button disabled={releaseBusy} onClick={closeRepoReleaseDialog} type="button" variant="outline">
                        Cancel
                      </Button>
                      <Button
                        aria-label="Release from version"
                        disabled={releaseBusy || !releaseTag.trim()}
                        type="submit"
                      >
                        {releaseBusy ? "Releasing..." : "Release"}
                      </Button>
                    </div>
                  </form>
                )}
              </Dialog>
            </section>
          ) : null}
          </ExtensionsCuratedPanel>
    </>
  ) : null;

  return (
    <OrganizationNavigation organizationId={organizationId} organizations={loadState.status === "loaded" ? loadState.organizations : undefined} onOrganizationChange={(nextOrganizationId) => {
      if (!organizationId || nextOrganizationId === organizationId) return;
      const href = organizationViewHref(nextOrganizationId, view);
      if (fileMutationBusy || validationBusy || publicationBusy || repoBusy) return;
      if (hasUnsavedChanges || repoHasUnsavedChanges) { setDiscardIntent({kind: "navigation", href}); return; }
      window.location.assign(href);
    }} view={view} onNavigate={(event, href) => {
      if (fileMutationBusy || validationBusy || publicationBusy || repoBusy) { event.preventDefault(); return; }
      if (hasUnsavedChanges || repoHasUnsavedChanges) { event.preventDefault(); setDiscardIntent({kind: "navigation", href}); }
    }} onSignOut={() => {
      if (fileMutationBusy || validationBusy || publicationBusy || repoBusy) return;
      if (hasUnsavedChanges || repoHasUnsavedChanges) setDiscardIntent({kind: "sign-out"});
      else void signOut();
    }}>
    <div className="space-y-8">
      <PageHeader
        title={organizationViews[view].title}
        description={
          organizationName
            ? `${organizationName} · ${organizationViews[view].description}`
            : organizationViews[view].description
        }
      />

      {loadState.status === "loading" ? (
        <p role="status" className="text-sm text-[var(--muted-foreground)]">
          Loading organization {organizationViews[view].title.toLowerCase()}...
        </p>
      ) : null}

      {loadState.status === "error" ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          Could not load this organization: {loadState.message}
        </p>
      ) : null}

      {actionError ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {actionError}
        </p>
      ) : null}

      {loadState.status === "loaded" ? (
        <>
          {view === "extensions" ? (
            <Tabs
              label="Extension views"
              tabs={EXTENSIONS_HUB_TABS}
              value={extensionsTab}
              onValueChange={(value) => onExtensionsTabChange?.(value as ExtensionsTab)}
            >
              {extensionsTab === "curated" ? authoringSections : null}
              {extensionsTab === "browse" ? (
                <ExtensionsBrowsePanel
                  organizationId={loadState.organizationId}
                  userId={state.status === "authenticated" ? state.user.id : ""}
                  canInstall={loadState.organizationRole === "owner"}
                  catalog={loadState.catalog}
                  installations={loadState.installations}
                  onInstalled={() => {
                    const organization = currentProjectContext();
                    if (organization) void refreshInstallations(organization);
                  }}
                />
              ) : null}
              {extensionsTab === "installed" ? (
                <ExtensionsInstalledPanel
                  organizationId={loadState.organizationId}
                  userId={state.status === "authenticated" ? state.user.id : ""}
                  canManage={loadState.organizationRole === "owner"}
                  organizationName={loadState.organizationName}
                  installations={loadState.installations}
                  updates={extensionUpdates}
                  updatingInstallationId={extensionUpdateBusyId}
                  onUpdate={(update) => void updateInstalledExtension(update)}
                  onRefreshInstallations={() => {
                    const organization = currentProjectContext();
                    return organization
                      ? refreshInstallations(organization)
                      : Promise.resolve(loadState.installations);
                  }}
                />
              ) : null}
              {extensionsTab === "updates" ? (
                <ExtensionsUpdatesPanel
                  updates={extensionUpdates}
                  canManage={loadState.organizationRole === "owner"}
                  updatingInstallationId={extensionUpdateBusyId}
                  onUpdate={(update) => void updateInstalledExtension(update)}
                />
              ) : null}
            </Tabs>
          ) : null}
          {extensionUpdateNotice ? (
            <p
              className={`text-sm ${extensionUpdateNotice.tone === "alert" ? "text-[var(--danger)]" : "text-[var(--muted-foreground)]"}`}
              role={extensionUpdateNotice.tone}
            >
              {extensionUpdateNotice.message}
            </p>
          ) : null}
          {view === "access" ? <section aria-labelledby="api-tokens-heading" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 id="api-tokens-heading" className="text-lg font-semibold">
                  Access · developer tokens
                </h2>
                <p className="text-sm text-[var(--muted-foreground)]">
                  API tokens keep their existing defaults. MCP tokens are limited to this organization, read-only, and expire after 30 days by default.
                </p>
              </div>
              <Button
                aria-label="Refresh developer tokens"
                disabled={loadState.apiTokens.status === "loading" || apiTokenBusy || revokingTokenId !== null}
                onClick={() => {
                  const organization = currentProjectContext();
                  if (organization) void refreshApiTokens(organization);
                }}
                variant="outline"
              >
                Refresh tokens
              </Button>
            </div>

            {apiTokenNotice ? (
              <p role="status" className="text-sm text-[var(--muted-foreground)]">
                {apiTokenNotice}
              </p>
            ) : null}

            <Card>
              <CardHeader>
                <div className="space-y-2">
                  <CardTitle>Create developer token</CardTitle>
                  <CardDescription>
                    Choose an API token or a manual MCP bearer token. Secrets are shown once after creation and are never stored by this page.
                  </CardDescription>
                  <p className="text-sm text-[var(--muted-foreground)]">
                    <strong>Least privilege:</strong> keep API access limited to this organization unless broader access is necessary.
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                <form
                  aria-label="Create personal API token"
                  className="grid gap-4"
                  onSubmit={(event) => void submitApiToken(event)}
                >
                  <label className="grid gap-1.5 text-sm font-medium">
                    Token name
                    <Input
                      aria-label="API token name"
                      autoComplete="off"
                      maxLength={80}
                      onChange={(event) => setApiTokenName(event.target.value)}
                      required
                      value={apiTokenName}
                    />
                  </label>
                  <label className="grid max-w-sm gap-1.5 text-sm font-medium">
                    Token audience
                    <Select
                      aria-label="Token audience"
                      onChange={(value) => {
                        if (value !== "api" && value !== "mcp") return;
                        setApiTokenAudience(value);
                        setApiTokenScopes(["build:read"]);
                                          }}
                      options={[
                        { value: "api", label: "API token" },
                        { value: "mcp", label: "MCP server token" },
                      ]}
                      value={apiTokenAudience}
                    />
                  </label>
                  <label className="grid max-w-xs gap-1.5 text-sm font-medium">
                    Expires in days
                    <Input
                      aria-label="Token expiry in days"
                      max={365}
                      min={1}
                      onChange={(event) => setApiTokenExpiryDays(event.target.value)}
                      required
                      type="number"
                      value={apiTokenExpiryDays}
                    />
                  </label>
                  {apiTokenAudience === "api" ? (
                    <>
                      <fieldset className="grid gap-2">
                        <legend className="text-sm font-medium">Allowed scopes</legend>
                        {API_TOKEN_SCOPE_OPTIONS.map((option) => (
                          <label className="flex items-start gap-2 text-sm" key={option.scope}>
                            <Input
                              aria-label={`API scope ${option.scope}`}
                              checked={apiTokenScopes.includes(option.scope)}
                              className="mt-1 size-4"
                              onChange={() => toggleApiTokenScope(option.scope)}
                              type="checkbox"
                            />
                            <span>
                              <span className="block font-medium">{option.label} <code>{option.scope}</code></span>
                              <span className="block text-[var(--muted-foreground)]">{option.detail}</span>
                            </span>
                          </label>
                        ))}
                        {apiTokenScopes.length === 0 ? (
                          <p role="alert" className="text-sm text-[var(--danger)]">
                            Select at least one developer scope.
                          </p>
                        ) : null}
                      </fieldset>
                      <p className="text-sm text-[var(--muted-foreground)]">
                        This token is scoped to {loadState.organizationName}. Member access is checked on every request.
                      </p>
                    </>
                  ) : (
                    <div className="space-y-2 rounded-md bg-[var(--muted)] p-3 text-sm">
                      <p><strong>Least privilege:</strong> exactly this organization and <code>build:read</code>; MCP tokens cannot write, run tests, request posting approvals, or access other organizations.</p>
                      <p>Authentication is manual custom Bearer, not OAuth. Clients that require OAuth discovery/authorization cannot use this token.</p>
                      <dl className="grid gap-2 sm:grid-cols-2">
                        <div>
                          <dt className="text-[var(--muted-foreground)]">MCP endpoint</dt>
                          <dd className="break-all"><code>{MCP_ENDPOINT_URL}</code></dd>
                        </div>
                        <div>
                          <dt className="text-[var(--muted-foreground)]">Bound resource audience</dt>
                          <dd className="break-all"><code>{MCP_RESOURCE_URI}</code></dd>
                        </div>
                      </dl>
                    </div>
                  )}
                  <div>
                    <Button
                      aria-label={apiTokenAudience === "mcp" ? "Create MCP token" : "Create API token"}
                      disabled={
                        apiTokenBusy ||
                        revokingTokenId !== null ||
                        !apiTokenName.trim() ||
                        apiTokenName.trim().length > 80 ||
                        !Number.isInteger(Number(apiTokenExpiryDays)) ||
                        Number(apiTokenExpiryDays) < 1 ||
                        Number(apiTokenExpiryDays) > 365 ||
                        (apiTokenAudience === "api" && apiTokenScopes.length === 0)
                      }
                      type="submit"
                    >
                      {apiTokenBusy
                        ? "Creating token..."
                        : apiTokenAudience === "mcp"
                          ? "Create MCP token"
                          : "Create API token"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {loadState.apiTokens.status === "loading" ? (
              <p role="status" className="text-sm text-[var(--muted-foreground)]">
                Loading developer tokens...
              </p>
            ) : null}
            {loadState.apiTokens.status === "error" ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {loadState.apiTokens.message}
              </p>
            ) : null}
            {loadState.apiTokens.status === "loaded" && loadState.apiTokens.items.length === 0 ? (
              <EmptyState
                title="No developer tokens in this organization"
                reason="Create a scoped API or MCP token above if a developer integration needs access."
              />
            ) : null}
            {loadState.apiTokens.status === "loaded" && loadState.apiTokens.items.length > 0 ? (
              <div className="grid gap-4">
                {loadState.apiTokens.items.map((token) => (
                  <Card key={token.id}>
                    <CardHeader>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <CardTitle>{token.name}</CardTitle>
                        <Badge tone="neutral">
                          {{ api: "API token", mcp: "MCP resource token", cli: "CLI extension token" }[token.audience]}
                        </Badge>
                        <Button
                          aria-label={`Revoke ${tokenAudienceName(token.audience)} token ${token.name}`}
                          disabled={apiTokenBusy || revokingTokenId !== null || token.organization_id !== loadState.organizationId}
                          onClick={() => setRevokeTokenIntent(token)}
                          size="sm"
                          variant="destructive"
                        >
                          Revoke
                        </Button>
                      </div>
                      <CardDescription>Created {token.created_at}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-3">
                      <dl className="grid gap-2 text-sm sm:grid-cols-2">
                        <div>
                          <dt className="text-[var(--muted-foreground)]">Expires</dt>
                          <dd>{token.expires_at ?? "No expiry returned by the API"}</dd>
                        </div>
                        <div>
                          <dt className="text-[var(--muted-foreground)]">Last used</dt>
                          <dd>{tokenLastUsedPresentation(token.last_used_at)}</dd>
                        </div>
                        <div>
                          <dt className="text-[var(--muted-foreground)]">Token ID</dt>
                          <dd className="break-all">{token.id}</dd>
                        </div>
                        <div>
                          <dt className="text-[var(--muted-foreground)]">Scopes</dt>
                          <dd className="break-words">
                            {token.scopes.length > 0
                              ? token.scopes.join(", ")
                              : "No developer scopes listed by the API"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-[var(--muted-foreground)]">Organization</dt>
                          <dd className="break-all">{loadState.organizationName}</dd>
                        </div>
                      </dl>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : null}
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle>OAuth grants</CardTitle>
                    <CardDescription>
                      Grants created through the local pre-registered MCP client. Each grants only <code>build:read</code> for one chosen organization; no refresh token is issued.
                    </CardDescription>
                  </div>
                  <Button
                    aria-label="Refresh MCP OAuth grants"
                    disabled={loadState.oauthGrants.status === "loading" || revokingMcpGrantId !== null}
                    onClick={() => {
                      const organization = currentProjectContext();
                      if (organization) void refreshMcpOAuthGrants(organization);
                    }}
                    variant="outline"
                  >
                    Refresh grants
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {mcpGrantNotice ? <p role="status" className="text-sm text-[var(--muted-foreground)]">{mcpGrantNotice}</p> : null}
                {loadState.oauthGrants.status === "loading" ? <p role="status" className="text-sm text-[var(--muted-foreground)]">Loading OAuth grants…</p> : null}
                {loadState.oauthGrants.status === "error" ? (
                  <p role="alert" className="text-sm text-[var(--danger)]">{loadState.oauthGrants.message}</p>
                ) : null}
                {loadState.oauthGrants.status === "loaded" && loadState.oauthGrants.items.length === 0 ? (
                  <EmptyState title="No active MCP OAuth grants" reason="The local pre-registered MCP client can request an organization-scoped grant when enabled. This does not enable OAuth discovery or general client registration." />
                ) : null}
                {loadState.oauthGrants.status === "loaded" && loadState.oauthGrants.items.length > 0 ? (
                  <div className="grid gap-3">
                    {loadState.oauthGrants.items.map((grant) => (
                      <Card key={grant.id}>
                        <CardHeader>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <CardTitle>{grant.client_name}</CardTitle>
                            <Button
                              aria-label={`Revoke MCP OAuth grant ${grant.id}`}
                              disabled={revokingMcpGrantId !== null}
                              onClick={() => setRevokeMcpGrantIntent(grant)}
                              size="sm"
                              variant="destructive"
                            >
                              Revoke grant
                            </Button>
                          </div>
                          <CardDescription>Created {grant.created_at}</CardDescription>
                        </CardHeader>
                        <CardContent className="pt-3">
                          <dl className="grid gap-2 text-sm sm:grid-cols-2">
                            <div><dt className="text-[var(--muted-foreground)]">Organization</dt><dd>{grant.organization_name}</dd></div>
                            <div><dt className="text-[var(--muted-foreground)]">Scope</dt><dd><code>{grant.scope}</code></dd></div>
                            <div><dt className="text-[var(--muted-foreground)]">Resource</dt><dd className="break-all"><code>{grant.resource_uri}</code></dd></div>
                            <div><dt className="text-[var(--muted-foreground)]">Access token expiry</dt><dd>{grant.expires_at ?? "No expiry returned"}</dd></div>
                          </dl>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : null}
                <p className="text-sm text-[var(--muted-foreground)]">
                  OAuth authorization here is only available when a local pre-registered client is configured. Public OAuth discovery, CIMD/DCR, general client registration, and refresh tokens are not enabled. Manual custom Bearer MCP tokens above remain a separate authentication mode.
                </p>
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  <div><dt className="text-[var(--muted-foreground)]">MCP endpoint</dt><dd className="break-all"><code>{MCP_ENDPOINT_URL}</code></dd></div>
                  <div><dt className="text-[var(--muted-foreground)]">Canonical resource</dt><dd className="break-all"><code>{MCP_RESOURCE_URI}</code></dd></div>
                </dl>
              </CardContent>
            </Card>
            <p className="text-sm text-[var(--muted-foreground)]">
              Last used records successful authentication, not whether a later permission check or action succeeded; updates are coalesced to at most once per minute.
            </p>
          </section>

          : null}
          <Dialog
            description={oneTimeToken?.response.audience === "mcp"
              ? "This MCP bearer secret is shown only once. Copy it now; closing this dialog clears it from the page."
              : "This secret is shown only once. Copy it now; closing this dialog clears it from the page."}
            onOpenChange={(open) => {
              if (!open) setOneTimeToken(null);
            }}
            open={oneTimeToken !== null}
            title={oneTimeToken?.response.audience === "mcp"
              ? "MCP token created — copy it now"
              : "API token created — copy it now"}
          >
            {oneTimeToken ? (
              <div className="space-y-4">
                <p className="break-all rounded-md bg-[var(--muted)] p-3 font-mono text-sm" aria-label="One-time API token secret">
                  {oneTimeToken.response.token}
                </p>
                <dl className="grid gap-2 text-sm">
                  <div>
                    <dt className="text-[var(--muted-foreground)]">Audience</dt>
                    <dd>{oneTimeToken.response.audience === "mcp" ? "MCP resource token" : "API token"}</dd>
                  </div>
                  <div>
                    <dt className="text-[var(--muted-foreground)]">Token name</dt>
                    <dd>{oneTimeToken.response.name}</dd>
                  </div>
                  <div>
                    <dt className="text-[var(--muted-foreground)]">Created</dt>
                    <dd>{oneTimeToken.createdAt ?? "Creation time is not available in the create response."}</dd>
                  </div>
                  <div>
                    <dt className="text-[var(--muted-foreground)]">Expires</dt>
                    <dd>{oneTimeToken.response.expires_at ?? "No expiry returned by the API"}</dd>
                  </div>
                  <div>
                    <dt className="text-[var(--muted-foreground)]">Scopes</dt>
                    <dd>{oneTimeToken.response.scopes.join(", ") || "No developer scopes returned"}</dd>
                  </div>
                  <div>
                    <dt className="text-[var(--muted-foreground)]">Organization</dt>
                    <dd>{loadState.organizationName}</dd>
                  </div>
                  {oneTimeToken.response.audience === "mcp" ? (
                    <>
                      <div>
                        <dt className="text-[var(--muted-foreground)]">MCP endpoint</dt>
                        <dd className="break-all"><code>{MCP_ENDPOINT_URL}</code></dd>
                      </div>
                      <div>
                        <dt className="text-[var(--muted-foreground)]">Bound resource audience</dt>
                        <dd className="break-all"><code>{MCP_RESOURCE_URI}</code></dd>
                      </div>
                      <div>
                        <dt className="text-[var(--muted-foreground)]">Authentication mode</dt>
                        <dd>Custom/manual Bearer</dd>
                      </div>
                      <div>
                        <dt className="text-[var(--muted-foreground)]">OAuth compatibility</dt>
                        <dd>This manual token is not an OAuth grant. OAuth discovery and general client registration are not enabled.</dd>
                      </div>
                    </>
                  ) : null}
                </dl>
                <p className="text-sm text-[var(--muted-foreground)]">
                  Store this secret in your approved secret manager; it is not saved by this page.
                </p>
              </div>
            ) : null}
          </Dialog>

          <Dialog
            description="Revoking removes this client's organization-scoped access token immediately. The MCP client will need a new authorization to reconnect."
            onOpenChange={(open) => {
              if (!open && revokingMcpGrantId === null) setRevokeMcpGrantIntent(null);
            }}
            open={revokeMcpGrantIntent !== null}
            title="Revoke this MCP OAuth grant?"
          >
            {revokeMcpGrantIntent ? (
              <div className="space-y-4">
                <p className="text-sm">{revokeMcpGrantIntent.client_name} · {revokeMcpGrantIntent.organization_name}</p>
                <div className="flex justify-end gap-2">
                  <Button disabled={revokingMcpGrantId !== null} onClick={() => setRevokeMcpGrantIntent(null)} variant="outline">Cancel</Button>
                  <Button
                    aria-label="Confirm OAuth grant revocation"
                    disabled={revokingMcpGrantId !== null}
                    onClick={() => void confirmMcpOAuthGrantRevocation()}
                    variant="destructive"
                  >
                    {revokingMcpGrantId !== null ? "Revoking grant…" : "Revoke grant"}
                  </Button>
                </div>
              </div>
            ) : null}
          </Dialog>

          <Dialog
            description={`Revoking this token immediately removes its ${tokenAudienceName(revokeTokenIntent?.audience ?? "api")} access. This cannot be undone.`}
            onOpenChange={(open) => {
              if (!open && revokingTokenId === null) setRevokeTokenIntent(null);
            }}
            open={revokeTokenIntent !== null}
            title={`Revoke this ${tokenAudienceName(revokeTokenIntent?.audience ?? "api")} token?`}
          >
            {revokeTokenIntent ? (
              <div className="space-y-4">
                <p className="text-sm">{revokeTokenIntent.name} · <code>{revokeTokenIntent.id}</code></p>
                <div className="flex justify-end gap-2">
                  <Button
                    disabled={revokingTokenId !== null}
                    onClick={() => setRevokeTokenIntent(null)}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    aria-label="Confirm token revocation"
                    disabled={revokingTokenId !== null}
                    onClick={() => void confirmTokenRevocation()}
                    variant="destructive"
                  >
                    {revokingTokenId !== null ? "Revoking token..." : "Revoke token"}
                  </Button>
                </div>
              </div>
            ) : null}
          </Dialog>
        </>
      ) : null}
    </div>
    </OrganizationNavigation>
  );
}
